import React, { useEffect, useMemo, useState, useRef } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPlus, faSearch, faVideo, faUserPlus, faArrowRightFromBracket, faCommentDots, faCircleDot, faHashtag, faPhone, faGear, faArrowLeft, faPaperPlane, faMicrophone, faXmark, faChevronLeft, faChevronRight, faEye, faTrash } from '@fortawesome/free-solid-svg-icons'
import { getAuth, onAuthStateChanged } from 'firebase/auth'
import { arrayUnion, collection, deleteDoc, doc, getDoc, getDocs, increment, limit, onSnapshot, orderBy, query, runTransaction, serverTimestamp, setDoc, where } from 'firebase/firestore'
import { getDatabase, ref, onValue, push, serverTimestamp as realtimeTimestamp, update, set, get, remove } from 'firebase/database'
import { deleteObject, getDownloadURL, ref as storageRef, uploadBytesResumable } from 'firebase/storage'
import swal from 'sweetalert'
import { toast } from 'react-toastify'
import { db as firestoreDb, storage } from '../firebase'
import logo from '../Images/logo-main-white.png'
import sampleImage from '../Images/defprofile.png'
import { useLocation, useNavigate } from 'react-router-dom'
import './WorkspaceCallsPage.css'
import './WorkspaceUpdatesPage.css'

const REACTION_OPTIONS = ["👍", "❤️", "😂", "🎯", "🔥"]
const CHANNEL_REACTION_SPECS = [
  { type: 'like', emoji: '👍', label: 'Like' },
  { type: 'love', emoji: '❤️', label: 'Love' },
  { type: 'laugh', emoji: '😂', label: 'Laugh' },
  { type: 'angry', emoji: '😡', label: 'Angry' },
]
const WORKSPACE_CALLS_API_URL = '/workspaceCallsApi'
const STATUS_TTL_MS = 24 * 60 * 60 * 1000
const STATUS_COLORS = ['#0F766E', '#1D4ED8', '#7C3AED', '#B45309', '#BE123C']
const MAX_STATUS_VIDEO_BYTES = 40 * 1024 * 1024
const DEFAULT_TEXT_BACKGROUND = '#1F2937'
const WORKSPACE_DAILY_API_KEY = process.env.REACT_APP_DAILY_API_KEY || '#'
const WORKSPACE_DAILY_DOMAIN = process.env.REACT_APP_DAILY_DOMAIN || 'https://ubunifucollege.daily.co'
const WORKSPACE_DAILY_API_URL = process.env.REACT_APP_DAILY_API_URL || 'https://api.daily.co/v1'

const firstNonEmpty = (...values) => {
  for (const value of values) {
    if (value === null || value === undefined) {
      continue
    }
    const text = String(value).trim()
    if (text) {
      return text
    }
  }
  return ''
}

const looksLikeWorkspaceUserId = (value) => {
  const text = String(value || '').trim()
  if (!text) {
    return false
  }
  if (text.includes(' ')) {
    return false
  }
  if (text.length < 10) {
    return false
  }
  return /^[A-Za-z0-9_-]+$/.test(text)
}

const firstDisplayableName = (...values) => {
  for (const value of values) {
    const text = String(value || '').trim()
    if (!text || looksLikeWorkspaceUserId(text)) {
      continue
    }
    return text
  }
  return ''
}

const normalizeStatusColor = (value) => {
  const candidate = firstNonEmpty(value, STATUS_COLORS[0])
  return /^#[0-9A-Fa-f]{6}$/.test(candidate) ? candidate : STATUS_COLORS[0]
}

const getFileExtension = (file) => {
  const name = String(file?.name || '').trim()
  const match = name.match(/\.([a-zA-Z0-9]+)$/)
  if (match) {
    return match[1].toLowerCase()
  }
  const mime = String(file?.type || '').trim().toLowerCase()
  if (mime.includes('/')) {
    return mime.split('/')[1] || 'jpg'
  }
  return 'jpg'
}

const getChannelReactionSpecByEmoji = (emoji) => CHANNEL_REACTION_SPECS.find((spec) => spec.emoji === emoji) || null

const buildDirectMessageSummary = (allChats, currentUserId, getMessageTimestamp) => {
  const conversationMap = new Map()
  const seenMessageIds = new Set()

  Object.entries(allChats || {}).forEach(([chatKey, messagesObj]) => {
    const participants = chatKey.split('_')
    if (!participants.includes(currentUserId)) return
    const otherUserId = participants.find((id) => id !== currentUserId)
    if (!otherUserId) return

    const conversationKey = [currentUserId, otherUserId].sort().join('__')
    const currentSummary = conversationMap.get(otherUserId) || {
      unreadCount: 0,
      lastMessage: null,
    }

    Object.entries(messagesObj || {}).forEach(([messageKey, message]) => {
      const uniqueMessageId = `${conversationKey}__${message?.messageId || messageKey}`
      if (seenMessageIds.has(uniqueMessageId)) return
      seenMessageIds.add(uniqueMessageId)

      if (message?.receiverId === currentUserId && !message?.isRead) {
        currentSummary.unreadCount += 1
      }

      if (!currentSummary.lastMessage || getMessageTimestamp(message) > getMessageTimestamp(currentSummary.lastMessage)) {
        currentSummary.lastMessage = message
      }
    })

    conversationMap.set(otherUserId, currentSummary)
  })

  const unreadMap = {}
  const lastMap = {}

  conversationMap.forEach((summary, otherUserId) => {
    unreadMap[otherUserId] = summary.unreadCount || 0
    if (summary.lastMessage) {
      lastMap[otherUserId] = {
        text: summary.lastMessage.text || "",
        timestamp: getMessageTimestamp(summary.lastMessage),
        senderId: summary.lastMessage.senderId || "",
      }
    }
  })

  return {
    userIds: Array.from(conversationMap.keys()),
    unreadMap,
    lastMap,
  }
}

function WorkspaceComp() {
  const auth = getAuth()
  const realtimeDb = getDatabase()
  const navigate = useNavigate()
  const location = useLocation()
  const longPressTimerRef = useRef(null)
  const typingHandlerRef = useRef(null)
  const pendingReactionOverridesRef = useRef({})

  const [channels, setChannels] = useState([])
  const [dmUsers, setDmUsers] = useState([])
  const [students, setStudents] = useState([])
  const [selectedChat, setSelectedChat] = useState(null)
  const [messages, setMessages] = useState([])
  const [newMessage, setNewMessage] = useState('')
  const [editingMessageId, setEditingMessageId] = useState('')
  const [editingMessageOriginalText, setEditingMessageOriginalText] = useState('')
  const [selectedAttachmentUri, setSelectedAttachmentUri] = useState(null)
  const [selectedAttachmentName, setSelectedAttachmentName] = useState('')
  const [selectedAttachmentType, setSelectedAttachmentType] = useState('')
  const [selectedAttachmentSizeBytes, setSelectedAttachmentSizeBytes] = useState(0)
  const [attachmentUploading, setAttachmentUploading] = useState(false)
  const [voiceRecording, setVoiceRecording] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [channelUnread, setChannelUnread] = useState({})
  const [dmUnread, setDmUnread] = useState({})
  const [dmLastMap, setDmLastMap] = useState({})
  const [workspaceChatThreads, setWorkspaceChatThreads] = useState([])
  const [workspaceChatThreadsLoading, setWorkspaceChatThreadsLoading] = useState(true)
  const [workspaceChatThreadsError, setWorkspaceChatThreadsError] = useState('')
  const [workspaceChatPresenceMap, setWorkspaceChatPresenceMap] = useState({})
  const [workspaceChatProfileMap, setWorkspaceChatProfileMap] = useState(new Map())
  const [channelReadAt, setChannelReadAt] = useState({})
  const [channelReadMap, setChannelReadMap] = useState({})
  const [channelMessagesMap, setChannelMessagesMap] = useState({})
  const [recentCalls, setRecentCalls] = useState([])
  const [callsLoading, setCallsLoading] = useState(true)
  const [callsError, setCallsError] = useState('')
  const [callProfiles, setCallProfiles] = useState(new Map())
  const [currentUserName, setCurrentUserName] = useState("Student")
  const [currentUserAvatar, setCurrentUserAvatar] = useState(sampleImage)
  const [currentUserCourse, setCurrentUserCourse] = useState("")
  const [currentUserStatus, setCurrentUserStatus] = useState("")
  const [currentUserId, setCurrentUserId] = useState("")
  const [statusMap, setStatusMap] = useState({})
  const [instructorDirectory, setInstructorDirectory] = useState(new Map())
  const [workspaceStatusFeed, setWorkspaceStatusFeed] = useState({ myStatus: null, recentStatuses: [] })
  const [workspaceStatusLoading, setWorkspaceStatusLoading] = useState(true)
  const [workspaceStatusError, setWorkspaceStatusError] = useState('')
  const [selectedWorkspaceStatusId, setSelectedWorkspaceStatusId] = useState('')
  const [workspaceStatusComposerOpen, setWorkspaceStatusComposerOpen] = useState(false)
  const [workspaceStatusComposerCaption, setWorkspaceStatusComposerCaption] = useState('')
  const [workspaceStatusComposerColorIndex, setWorkspaceStatusComposerColorIndex] = useState(0)
  const [workspaceStatusComposerMediaFile, setWorkspaceStatusComposerMediaFile] = useState(null)
  const [workspaceStatusComposerMediaType, setWorkspaceStatusComposerMediaType] = useState('')
  const [workspaceStatusComposerPreviewUrl, setWorkspaceStatusComposerPreviewUrl] = useState('')
  const [workspaceStatusComposerPublishing, setWorkspaceStatusComposerPublishing] = useState(false)
  const [workspaceStatusComposerProgress, setWorkspaceStatusComposerProgress] = useState(0)
  const [workspaceStatusViewerOpen, setWorkspaceStatusViewerOpen] = useState(false)
  const [workspaceStatusViewerUserId, setWorkspaceStatusViewerUserId] = useState('')
  const [workspaceStatusViewerInitialStatusId, setWorkspaceStatusViewerInitialStatusId] = useState('')
  const [workspaceStatusViewerStories, setWorkspaceStatusViewerStories] = useState([])
  const [workspaceStatusViewerIndex, setWorkspaceStatusViewerIndex] = useState(0)
  const [workspaceStatusViewerLoading, setWorkspaceStatusViewerLoading] = useState(false)
  const [workspaceStatusViewersOpen, setWorkspaceStatusViewersOpen] = useState(false)
  const [workspaceStatusViewersLoading, setWorkspaceStatusViewersLoading] = useState(false)
  const [workspaceStatusViewersStatus, setWorkspaceStatusViewersStatus] = useState(null)
  const [workspaceStatusViewers, setWorkspaceStatusViewers] = useState([])
  const [activeReactionPickerId, setActiveReactionPickerId] = useState("")
  const [reactionDocuments, setReactionDocuments] = useState({})
  const [pendingReactionOverrides, setPendingReactionOverrides] = useState({})
  const [pendingDMTarget, setPendingDMTarget] = useState(() => location.state?.openDMUserId || "")
  const [activeWorkspaceSection, setActiveWorkspaceSection] = useState('chats')
  const messagesEndRef = useRef(null)
  const editorRef = useRef(null)
  const statusMediaInputRef = useRef(null)
  const viewerViewedStoryIdsRef = useRef(new Set())
  const DEFAULT_CHANNEL_NAMES = ["genral", "general"]
  const [debugDM, setDebugDM] = useState(false)
  const canUseWorkspace = currentUserStatus.trim().toLowerCase() === "student"
  const workspaceChatThreadsLoadedRef = useRef(false)

  useEffect(() => {
    setPendingDMTarget(location.state?.openDMUserId || "")
  }, [location.state])

  useEffect(() => {
    if (typeof window === "undefined") return
    const readDebugFlag = () => {
      const fromStorage = window.localStorage?.getItem("debugDM") === "1"
      const fromQuery = new URLSearchParams(window.location.search).get("debugDM") === "1"
      setDebugDM(fromStorage || fromQuery)
    }
    readDebugFlag()
    const handleStorage = (event) => {
      if (event.key === "debugDM") readDebugFlag()
    }
    const handleDebugEvent = () => readDebugFlag()
    window.addEventListener("storage", handleStorage)
    window.addEventListener("debugDM", handleDebugEvent)
    return () => {
      window.removeEventListener("storage", handleStorage)
      window.removeEventListener("debugDM", handleDebugEvent)
    }
  }, [])

  const isDefaultChannel = (channel) => {
    if (!channel) return false
    if (channel.isDefault) return true
    const name = firstNonEmpty(channel?.name, channel?.teamName).trim().toLowerCase()
    return DEFAULT_CHANNEL_NAMES.includes(name)
  }

  const hasCustomAvatar = (member) => {
    if (!member?.avatar) return false
    return member.avatar !== sampleImage
  }

  const formatWorkspaceStatusTime = (timestampMs) => {
    if (!timestampMs) {
      return 'Just now'
    }
    const diff = Date.now() - timestampMs
    if (diff < 60 * 1000) {
      return 'Just now'
    }
    if (diff < 60 * 60 * 1000) {
      const minutes = Math.max(1, Math.floor(diff / (60 * 1000)))
      return `${minutes}m ago`
    }
    if (diff < 24 * 60 * 60 * 1000) {
      const hours = Math.max(1, Math.floor(diff / (60 * 60 * 1000)))
      return `${hours}h ago`
    }
    return new Date(timestampMs).toLocaleDateString('en-US', {
      day: '2-digit',
      month: 'short',
      hour: 'numeric',
      minute: 'numeric',
    })
  }

  const formatWorkspaceStatusClock = (timestampMs) => {
    if (!timestampMs) return ''
    return new Date(timestampMs).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
    })
  }

  const formatStoryTime = formatWorkspaceStatusClock

  const getInitials = (value) => {
    const trimmed = firstNonEmpty(value)
    if (!trimmed) return 'C'
    const parts = trimmed.split(/\s+/).filter(Boolean)
    if (parts.length === 1) {
      return trimmed.substring(0, Math.min(2, trimmed.length)).toUpperCase()
    }
    return parts
      .map((part) => part.trim().charAt(0))
      .filter(Boolean)
      .slice(0, 2)
      .join('')
      .toUpperCase()
  }

  const formatWorkspaceChatTime = (timestampMs) => {
    if (!timestampMs) return ''
    return new Date(timestampMs).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
    })
  }

  const getWorkspaceConversationId = (firstUserId, secondUserId) => {
    const ids = [firstUserId, secondUserId]
      .filter(Boolean)
      .map((value) => String(value).trim())
      .filter(Boolean)
    if (ids.length !== 2) {
      return ''
    }
    return ids.sort().join('_')
  }

  const getWorkspaceChatAttachmentLabel = (attachmentType, attachmentName) => {
    const normalized = firstNonEmpty(attachmentType).toLowerCase()
    if (normalized === 'image') return 'Photo'
    if (normalized === 'video') return 'Video'
    if (normalized === 'audio') return 'Voice message'
    if (normalized === 'file') return firstNonEmpty(attachmentName, 'File')
    return ''
  }

  const getWorkspaceChatMessagePreview = (message = {}) => {
    const attachmentLabel = getWorkspaceChatAttachmentLabel(message.attachmentType, message.attachmentName)
    if (attachmentLabel) {
      return message.edited || message.isEdited ? `${attachmentLabel} (edited)` : attachmentLabel
    }
    const plain = getPlainTextFromHtml(firstNonEmpty(message.text, ''))
    const base = plain.replace(/\s+/g, ' ').trim()
    if (!base) {
      return message.edited || message.isEdited ? 'Message (edited)' : 'Message'
    }
    return message.edited || message.isEdited ? `${base} (edited)` : base
  }

  const resolveWorkspaceChatProfile = (userId) => {
    if (!userId) {
      return {
        userId: '',
        displayName: 'Conversation',
        avatarUrl: sampleImage,
        course: '',
        phone: '',
      }
    }

    const profile = workspaceChatProfileMap.get(userId) || memberDirectory.get(userId) || instructorDirectory.get(userId) || null
    return {
      userId,
      displayName: firstDisplayableName(
        profile?.bio,
        profile?.displayName,
        profile?.name,
        profile?.fullName,
        profile?.userName,
        profile?.employeeName,
      ),
      avatarUrl: firstNonEmpty(profile?.profilePic, profile?.avatarUrl, sampleImage),
      course: firstNonEmpty(profile?.course, ''),
      phone: firstNonEmpty(profile?.phone, profile?.phoneNumber, ''),
    }
  }

  const buildWorkspaceChatThreadSummary = (chatSnapshot) => {
    const chatId = firstNonEmpty(chatSnapshot.key, '')
    if (!chatId) {
      return null
    }

    const data = chatSnapshot.val() || {}
    const summary = data.summary || {}
    const messagesSnapshot = chatSnapshot.child('messages')
    if (!messagesSnapshot.exists()) {
      return null
    }

    const participantsSnapshot = chatSnapshot.child('participants')
    const participantIds = []

    participantsSnapshot.forEach((participantSnapshot) => {
      const participantId = firstNonEmpty(participantSnapshot.key, '')
      if (participantId) {
        participantIds.push(participantId)
      }
    })

    const messageSnapshots = []
    messagesSnapshot.forEach((messageSnapshot) => {
      messageSnapshots.push(messageSnapshot)
    })

    const normalizedChatParts = chatId.split('_').map((part) => part.trim()).filter(Boolean)
    const otherUserId =
      participantIds.find((participantId) => participantId !== currentUserId) ||
      messageSnapshots.flatMap((messageSnapshot) => [
        firstNonEmpty(messageSnapshot.child('senderId').val(), ''),
        firstNonEmpty(messageSnapshot.child('receiverId').val(), ''),
      ]).find((participantId) => participantId && participantId !== currentUserId) ||
      normalizedChatParts.find((part) => part !== currentUserId) ||
      ''

    if (!otherUserId) {
      return null
    }

    let latestMessage = null
    let unreadCount = 0

    messageSnapshots.forEach((messageSnapshot) => {
      const message = {
        messageId: firstNonEmpty(messageSnapshot.child('messageId').val(), messageSnapshot.key, ''),
        senderId: firstNonEmpty(messageSnapshot.child('senderId').val(), ''),
        receiverId: firstNonEmpty(messageSnapshot.child('receiverId').val(), ''),
        text: firstNonEmpty(messageSnapshot.child('text').val(), ''),
        clientTimestampMs: getTimestamp(
          messageSnapshot.child('clientTimestamp').val() || messageSnapshot.child('serverTimestamp').val()
        ),
        serverTimestampMs: getTimestamp(messageSnapshot.child('serverTimestamp').val()),
        read: Boolean(messageSnapshot.child('read').val()) || Boolean(messageSnapshot.child('isRead').val()),
        readAtMs: getTimestamp(messageSnapshot.child('readAt').val()),
        edited: Boolean(messageSnapshot.child('edited').val()) || Boolean(messageSnapshot.child('isEdited').val()),
        attachmentUrl: firstNonEmpty(messageSnapshot.child('attachmentUrl').val(), ''),
        attachmentName: firstNonEmpty(messageSnapshot.child('attachmentName').val(), ''),
        attachmentType: firstNonEmpty(messageSnapshot.child('attachmentType').val(), ''),
        attachmentSizeBytes: Number(firstNonEmpty(messageSnapshot.child('attachmentSizeBytes').val(), 0)) || 0,
      }
      message.mine = currentUserId === message.senderId
      message.previewText = getWorkspaceChatMessagePreview(message)

      if (!latestMessage || message.clientTimestampMs >= latestMessage.clientTimestampMs) {
        latestMessage = message
      }
      if (currentUserId === message.receiverId && !message.read) {
        unreadCount += 1
      }
    })

    if (!latestMessage) {
      return null
    }

    const profile = resolveWorkspaceChatProfile(otherUserId)
    const summaryLastMessageText = firstNonEmpty(summary.lastMessageText, latestMessage.previewText)
    const summaryLastSenderId = firstNonEmpty(summary.lastSenderId, latestMessage.senderId)
    const lastMessageMine = summaryLastSenderId === currentUserId || latestMessage.mine
    const preview = summaryLastMessageText
      ? (lastMessageMine && !summaryLastMessageText.startsWith('You: ')
        ? `You: ${summaryLastMessageText}`
        : summaryLastMessageText)
      : latestMessage.previewText

    return {
      chatId,
      otherUserId,
      displayName: firstDisplayableName(
        profile.displayName,
        !lastMessageMine ? firstDisplayableName(summary.lastSenderName, latestMessage.senderName) : '',
      ),
      avatarUrl: profile.avatarUrl,
      course: profile.course,
      phone: profile.phone,
      preview,
      timestampMs: latestMessage.clientTimestampMs,
      unreadCount,
      lastMessageMine,
    }
  }

  const getChannelMemberList = (channel) => {
    if (!channel) return []
    let memberIds = []
    if (isDefaultChannel(channel)) {
      memberIds = Array.from(memberDirectory.keys())
    } else {
      memberIds = Array.isArray(channel.members) ? channel.members : []
    }
    const members = memberIds.map((uid) => {
      const user = memberDirectory.get(uid)
      return {
        id: uid,
        name: user?.bio || "Student",
        avatar: user?.profilePic || sampleImage,
      }
    })
    return [...members].sort((a, b) => {
      const aHas = hasCustomAvatar(a)
      const bHas = hasCustomAvatar(b)
      if (aHas === bHas) return 0
      return aHas ? -1 : 1
    })
  }

  const getChannelMemberDisplay = (channel) => {
    const all = getChannelMemberList(channel)
    const shown = all.slice(0, 5)
    const remaining = Math.max(0, all.length - shown.length)
    return { members: shown, remaining, all }
  }

  const getChannelMemberPreview = (channel) => {
    const all = getChannelMemberList(channel)
    const shown = all.slice(0, 3)
    const remaining = Math.max(0, all.length - shown.length)
    return { shown, remaining }
  }

  const sanitizeMessageHtml = (input) => {
    if (!input) return ""
    const parser = new DOMParser()
    const doc = parser.parseFromString(input, "text/html")
    const allowedTags = new Set([
      "b",
      "strong",
      "i",
      "em",
      "u",
      "s",
      "code",
      "pre",
      "br",
      "p",
      "div",
      "ul",
      "ol",
      "li",
      "blockquote",
      "span",
    ])

    const walk = (node) => {
      Array.from(node.childNodes).forEach((child) => {
        if (child.nodeType === Node.ELEMENT_NODE) {
          const tag = child.tagName.toLowerCase()
          if (!allowedTags.has(tag)) {
            const fragment = doc.createDocumentFragment()
            while (child.firstChild) {
              fragment.appendChild(child.firstChild)
            }
            child.replaceWith(fragment)
            return
          }
          Array.from(child.attributes).forEach((attr) => child.removeAttribute(attr.name))
          walk(child)
        } else if (child.nodeType === Node.COMMENT_NODE) {
          child.remove()
        }
      })
    }

    walk(doc.body)
    return doc.body.innerHTML
  }

  const getPlainTextFromHtml = (input) => {
    if (!input) return ""
    const parser = new DOMParser()
    const doc = parser.parseFromString(input, "text/html")
    return (doc.body.textContent || "").replace(/\u00a0/g, " ")
  }

  const updateComposerButtons = () => {}

  const updateVoiceButtonState = () => {}

  const showRecordingUi = () => {}

  const clearSelectedAttachment = () => {
    setSelectedAttachmentUri(null)
    setSelectedAttachmentName('')
    setSelectedAttachmentType('')
    setSelectedAttachmentSizeBytes(0)
  }

  const clearEditMode = () => {
    setEditingMessageId('')
    setEditingMessageOriginalText('')
    setNewMessage('')
    if (editorRef.current) {
      editorRef.current.innerHTML = ''
    }
  }

  const beginEditMode = (message) => {
    if (!message?.messageId) return
    setEditingMessageId(message.messageId)
    setEditingMessageOriginalText(message.text || '')
    setNewMessage(message.text || '')
    if (editorRef.current) {
      editorRef.current.innerHTML = message.text || ''
      editorRef.current.focus()
    }
  }

  const updateMyPresence = (typing) => {
    if (!currentUserId) return
    if (typeof navigator !== 'undefined' && navigator.onLine === false) return
    update(ref(realtimeDb, `workspacePresence/${currentUserId}`), {
      online: true,
      typing: Boolean(typing),
      lastSeenAt: Date.now(),
    })
  }

  const updateTypingState = (typing) => {
    if (typingHandlerRef.current) {
      window.clearTimeout(typingHandlerRef.current)
      typingHandlerRef.current = null
    }
    updateMyPresence(typing)
    if (typing) {
      typingHandlerRef.current = window.setTimeout(() => {
        updateMyPresence(false)
        typingHandlerRef.current = null
      }, 1500)
    }
  }

  const toggleVoiceRecording = () => {
    toast.info('Voice recording is not available in the web workspace yet.')
  }

  const rebuildWorkspaceConversationSummary = async (chatId, currentUserIdValue, otherUserIdValue) => {
    const normalizedChatId = firstNonEmpty(chatId, getWorkspaceConversationId(currentUserIdValue, otherUserIdValue))
    if (!normalizedChatId) return

    try {
      const snapshot = await get(ref(realtimeDb, `workspaceChats/${normalizedChatId}/messages`))
      let latestMessage = null
      snapshot.forEach((childSnapshot) => {
        const message = {
          id: firstNonEmpty(childSnapshot.key, ''),
          ...childSnapshot.val(),
        }
        if (!latestMessage || getMessageTimestamp(message) >= getMessageTimestamp(latestMessage)) {
          latestMessage = message
        }
      })

      const updates = {}
      const summaryRoot = `workspaceChats/${normalizedChatId}/summary`
      if (!latestMessage) {
        updates[`${summaryRoot}/lastMessageText`] = ''
        updates[`${summaryRoot}/lastMessageTimestamp`] = 0
        updates[`${summaryRoot}/lastSenderId`] = ''
        updates[`${summaryRoot}/lastSenderName`] = ''
        updates[`${summaryRoot}/updatedAt`] = Date.now()
      } else {
        const preview = getWorkspaceChatMessagePreview(latestMessage)
        updates[`${summaryRoot}/lastMessageText`] = preview
        updates[`${summaryRoot}/lastMessageTimestamp`] = getMessageTimestamp(latestMessage)
        updates[`${summaryRoot}/lastSenderId`] = firstNonEmpty(latestMessage.senderId, '')
        updates[`${summaryRoot}/lastSenderName`] = latestMessage.senderId === currentUserIdValue
          ? 'You'
          : firstNonEmpty(latestMessage.senderName, '')
        updates[`${summaryRoot}/updatedAt`] = Date.now()
      }
      await update(ref(realtimeDb), updates)
    } catch (error) {
      console.warn('Could not rebuild workspace chat summary', error)
    }
  }

  const rebuildWorkspaceChannelSummary = async (channelId) => {
    const normalizedChannelId = firstNonEmpty(channelId, '')
    if (!normalizedChannelId) return

    try {
      const snapshot = await get(ref(realtimeDb, `workspaceChannels/${normalizedChannelId}/messages`))
      let latestMessage = null
      snapshot.forEach((childSnapshot) => {
        const message = normalizeWorkspaceMessage(childSnapshot.key, childSnapshot.val(), normalizedChannelId)
        if (!latestMessage || getMessageTimestamp(message) >= getMessageTimestamp(latestMessage)) {
          latestMessage = message
        }
      })

      const updates = {}
      const summaryRoot = `workspaceChannels/${normalizedChannelId}`
      if (!latestMessage) {
        updates[`${summaryRoot}/lastMessageText`] = ''
        updates[`${summaryRoot}/lastMessageAt`] = 0
        updates[`${summaryRoot}/lastSenderId`] = ''
        updates[`${summaryRoot}/lastSenderName`] = ''
      } else {
        const preview = latestMessage.systemMessage
          ? firstNonEmpty(latestMessage.text, 'Message')
          : latestMessage.attachmentUrl.trim()
            ? getWorkspaceChatMessagePreview(latestMessage)
            : firstNonEmpty(getPlainTextFromHtml(latestMessage.text), latestMessage.text, 'Message')
        updates[`${summaryRoot}/lastMessageText`] = latestMessage.isEdited ? `${preview} (edited)` : preview
        updates[`${summaryRoot}/lastMessageAt`] = getMessageTimestamp(latestMessage)
        updates[`${summaryRoot}/lastSenderId`] = latestMessage.senderId
        updates[`${summaryRoot}/lastSenderName`] = latestMessage.senderName
      }
      await update(ref(realtimeDb), updates)
    } catch (error) {
      console.warn('Could not rebuild workspace channel summary', error)
    }
  }

  const toggleChannelReaction = async (message, emoji) => {
    if (!selectedChat || selectedChat.type !== 'channel' || !currentUserId || !message) return
    const messageId = firstNonEmpty(message.messageId, message.id, '')
    const channelId = firstNonEmpty(selectedChat.channelId, '')
    if (!messageId || !channelId) return

    const spec = getChannelReactionSpecByEmoji(emoji)
    if (!spec) return

    const existingReaction = message.reactionsByUser?.[currentUserId] || message.reactions?.[currentUserId] || null
    const reactionRef = ref(realtimeDb, `workspaceChannels/${channelId}/messages/${messageId}/reactions/${currentUserId}`)
    if (existingReaction?.emoji === spec.emoji) {
      await remove(reactionRef)
      return
    }

    await update(reactionRef, {
      type: spec.type,
      emoji: spec.emoji,
      label: spec.label,
      updatedAt: Date.now(),
    })
  }

  const openWorkspaceMessageActions = async (message) => {
    const messageId = firstNonEmpty(message?.messageId, message?.id, '')
    if (!messageId) return
    if (selectedChat?.type === 'channel') {
      if (message?.systemMessage) {
        return
      }
      if (!message.isMine) {
        openReactionPicker(messageId)
        return
      }

      const choice = await swal({
        title: 'Message options',
        buttons: {
          cancel: {
            text: 'Cancel',
            value: null,
            visible: true,
          },
          react: {
            text: 'React',
            value: 'react',
          },
          edit: {
            text: 'Edit',
            value: 'edit',
          },
          delete: {
            text: 'Delete',
            value: 'delete',
            className: 'swal-button--danger',
          },
        },
      })

      if (choice === 'react') {
        openReactionPicker(messageId)
        return
      }
      if (choice === 'edit') {
        beginEditMode(message)
        return
      }
      if (choice !== 'delete') {
        return
      }

      const confirmed = await swal({
        title: 'Delete message?',
        text: 'This will remove the message for everyone.',
        icon: 'warning',
        buttons: ['Cancel', 'Delete'],
        dangerMode: true,
      })
      if (!confirmed) return

      try {
        await remove(ref(realtimeDb, `workspaceChannels/${selectedChat.channelId}/messages/${messageId}`))
        await rebuildWorkspaceChannelSummary(selectedChat.channelId)
        toast.success('Message deleted')
      } catch (error) {
        toast.error('We could not delete that message.')
      }
      return
    }
    if (!message.isMine) {
      openReactionPicker(messageId)
      return
    }

    const choice = await swal({
      title: 'Message options',
      buttons: {
        cancel: {
          text: 'Cancel',
          value: null,
          visible: true,
        },
        edit: {
          text: 'Edit',
          value: 'edit',
        },
        delete: {
          text: 'Unsend',
          value: 'delete',
          className: 'swal-button--danger',
        },
      },
    })

    if (choice === 'edit') {
      beginEditMode(message)
      return
    }

    if (choice === 'delete') {
      if (typeof navigator !== 'undefined' && navigator.onLine === false) {
        toast('You are offline')
        return
      }
      const confirmed = await swal({
        title: 'Unsend message?',
        text: 'This will remove the message for everyone.',
        icon: 'warning',
        buttons: ['Cancel', 'Unsend'],
        dangerMode: true,
      })
      if (!confirmed) return

      const chatId = firstNonEmpty(selectedChat?.chatId, getWorkspaceConversationId(currentUserId, selectedChat?.userId))
      if (!chatId) {
        toast.error('We could not delete that message.')
        return
      }
      try {
        await remove(ref(realtimeDb, `workspaceChats/${chatId}/messages/${messageId}`))
        await rebuildWorkspaceConversationSummary(chatId, currentUserId, selectedChat?.userId || '')
        toast.success('Message unsent')
      } catch (error) {
        toast.error('We could not delete that message.')
      }
    }
  }

  const openWorkspaceConversationActions = async () => {
    if (!selectedChat || selectedChat.type !== 'dm') return

    const choice = await swal({
      title: firstNonEmpty(selectedChat.name, 'Conversation'),
      buttons: {
        cancel: {
          text: 'Cancel',
          value: null,
          visible: true,
        },
        delete: {
          text: 'Delete chat',
          value: 'delete',
          className: 'swal-button--danger',
        },
      },
    })

    if (choice !== 'delete') return

    const confirmed = await swal({
      title: 'Delete this chat?',
      text: 'This will remove the whole conversation from the workspace.',
      icon: 'warning',
      buttons: ['Cancel', 'Delete'],
      dangerMode: true,
    })

    if (!confirmed) return

    const chatId = firstNonEmpty(selectedChat.chatId, getWorkspaceConversationId(currentUserId, selectedChat.userId))
    if (!chatId) return

    try {
      await remove(ref(realtimeDb, `workspaceChats/${chatId}`))
      setSelectedChat(null)
      setActiveWorkspaceSection('chats')
      setMessages([])
      clearEditMode()
      clearSelectedAttachment()
      setActiveReactionPickerId('')
      toast.success('Chat deleted')
    } catch (error) {
      toast.error('We could not delete this chat.')
    }
  }

  const openAttachmentUrl = (attachmentUrl) => {
    const url = firstNonEmpty(attachmentUrl, '')
    if (!url) return
    if (typeof window === 'undefined') return
    const opened = window.open(url, '_blank', 'noopener,noreferrer')
    if (!opened) {
      toast.error('We could not open that attachment.')
    }
  }

  const openAudioUrl = (attachmentUrl) => {
    openAttachmentUrl(attachmentUrl)
  }

  const openAttachmentPreview = (attachmentType, attachmentUrl, attachmentTitle) => {
    const url = firstNonEmpty(attachmentUrl, '')
    if (!url || typeof window === 'undefined') return

    const previewWindow = window.open('', '_blank', 'noopener,noreferrer')
    if (!previewWindow) {
      openAttachmentUrl(url)
      return
    }

    const safeTitle = firstNonEmpty(attachmentTitle, 'Attachment')
    const normalizedType = firstNonEmpty(attachmentType, '').toLowerCase()
    const mediaMarkup = normalizedType === 'video'
      ? `<video controls autoplay style="max-width:100%;max-height:78vh;border-radius:18px;background:#000;"><source src="${url}" /></video>`
      : `<img src="${url}" alt="${safeTitle}" style="max-width:100%;max-height:78vh;object-fit:contain;border-radius:18px;" />`

    previewWindow.document.open()
    previewWindow.document.write(`
      <!doctype html>
      <html>
        <head>
          <meta charset="utf-8" />
          <meta name="viewport" content="width=device-width, initial-scale=1" />
          <title>${safeTitle}</title>
          <style>
            html, body {
              margin: 0;
              width: 100%;
              height: 100%;
              background: #0f172a;
              color: #fff;
              font-family: system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
            }
            body {
              display: grid;
              place-items: center;
              padding: 24px;
              box-sizing: border-box;
            }
            .preview {
              max-width: min(100%, 960px);
              text-align: center;
            }
            h1 {
              margin: 0 0 18px;
              font-size: 20px;
              font-weight: 700;
            }
            p {
              margin: 14px 0 0;
              color: #cbd5e1;
              font-size: 14px;
            }
          </style>
        </head>
        <body>
          <div class="preview">
            <h1>${safeTitle}</h1>
            ${mediaMarkup}
            <p>Open in the workspace attachment viewer on mobile for the full experience.</p>
          </div>
        </body>
      </html>
    `)
    previewWindow.document.close()
  }

  const updateEditorState = () => {
    if (!editorRef.current) return
    const nextHtml = editorRef.current.innerHTML
    setNewMessage(nextHtml)
    updateTypingState(Boolean(getPlainTextFromHtml(nextHtml).trim()))
  }

  const applyInlineStyle = (command) => {
    if (!editorRef.current) return
    editorRef.current.focus()
    document.execCommand(command, false, null)
    updateEditorState()
  }

  const insertCodeBlock = () => {
    if (!editorRef.current) return
    editorRef.current.focus()
    document.execCommand("insertHTML", false, "<pre><code>\n</code></pre>")
    updateEditorState()
  }

  const filteredChannels = useMemo(() => {
    if (!searchTerm.trim()) return channels
    return channels.filter((channel) =>
      [
        channel.name,
        channel.teamName,
        channel.description,
        channel.teamDesc,
        channel.lastMessageText,
        channel.createdByName,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
        .includes(searchTerm.toLowerCase())
    )
  }, [channels, searchTerm])

  const visibleChannels = useMemo(() => {
    const uid = currentUserId
    const list = !uid
      ? filteredChannels
      : filteredChannels.filter((channel) => {
          if (isDefaultChannel(channel)) return true
          if (channel.isPublic) return true
          if (!Array.isArray(channel.members) || channel.members.length === 0) return false
          return channel.members.includes(uid)
        })
    return [...list].sort((a, b) => {
      return Number(b.lastMessageAtMs || 0) - Number(a.lastMessageAtMs || 0)
    })
  }, [filteredChannels, currentUserId])

  const mergedDMs = useMemo(() => {
    const map = new Map()
    students.forEach((student) => {
      map.set(student.userId, student)
    })
    dmUsers.forEach((user) => {
      map.set(user.userId, user)
    })
    return Array.from(map.values())
  }, [students, dmUsers])

  const instructorContacts = useMemo(() => (
    Array.from(instructorDirectory.values())
      .filter((instructor) => instructor?.userId && instructor.userId !== currentUserId)
      .map((instructor) => ({
        id: `employee-${instructor.userId}`,
        userId: instructor.userId,
        bio: instructor.bio || "Instructor",
        profilePic: instructor.profilePic || sampleImage,
        course: "Instructor",
        senderRole: "instructor",
      }))
  ), [instructorDirectory, currentUserId])

  const newChatContacts = useMemo(() => {
    return Array.from(
      new Map(
        [...students, ...instructorContacts]
          .filter((contact) => contact?.userId)
          .map((contact) => [contact.userId, contact])
      ).values()
    ).sort((left, right) => {
      const leftName = firstNonEmpty(left.bio, left.displayName, left.name, left.fullName, left.userName, '')
      const rightName = firstNonEmpty(right.bio, right.displayName, right.name, right.fullName, right.userName, '')
      return leftName.localeCompare(rightName)
    })
  }, [students, instructorContacts])

  const dmUserIds = useMemo(() => {
    return new Set(dmUsers.map((user) => user.userId).filter(Boolean))
  }, [dmUsers])

  const filteredDMs = useMemo(() => {
    const term = searchTerm.trim().toLowerCase()
    const source = term
      ? Array.from(new Map(
        [...mergedDMs, ...instructorContacts].map((user) => [user.userId, user])
      ).values())
      : mergedDMs

    if (!term) return source
    return source.filter((user) =>
      user.bio?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.course?.toLowerCase().includes(searchTerm.toLowerCase())
    )
  }, [mergedDMs, instructorContacts, searchTerm])

  const sortedDMs = useMemo(() => {
    return [...filteredDMs].sort((a, b) => {
      const aEntry = dmLastMap[a.userId]
      const bEntry = dmLastMap[b.userId]
      const aLast = aEntry?.timestamp || 0
      const bLast = bEntry?.timestamp || 0
      const aInteracted = Boolean(aEntry) || dmUserIds.has(a.userId)
      const bInteracted = Boolean(bEntry) || dmUserIds.has(b.userId)
      if (aInteracted !== bInteracted) return aInteracted ? -1 : 1
      if (aLast !== bLast) return bLast - aLast
      const aUnread = dmUnread[a.userId] || 0
      const bUnread = dmUnread[b.userId] || 0
      if (aUnread !== bUnread) return bUnread - aUnread
      return (a.bio || "").localeCompare(b.bio || "")
    })
  }, [filteredDMs, dmUnread, dmLastMap, dmUserIds])

  const sidebarDMs = useMemo(() => {
    const map = new Map()
    mergedDMs.forEach((user) => {
      if (user?.userId) map.set(user.userId, user)
    })

    const activeUserIds = new Set([
      ...Object.keys(dmLastMap || {}),
      ...Object.keys(dmUnread || {})
    ].filter(Boolean))

    activeUserIds.forEach((userId) => {
      if (map.has(userId)) return
      const instructor = instructorDirectory.get(userId)
      if (instructor) {
        map.set(userId, {
          id: `employee-${userId}`,
          userId,
          bio: instructor.bio || "Instructor",
          profilePic: instructor.profilePic || sampleImage,
          course: "Instructor",
          senderRole: "instructor",
        })
        return
      }

      map.set(userId, {
        id: `dm-${userId}`,
        userId,
        bio: "Conversation",
        profilePic: sampleImage,
        course: "",
      })
    })

    return [...map.values()].sort((a, b) => {
      const aEntry = dmLastMap[a.userId]
      const bEntry = dmLastMap[b.userId]
      const aLast = aEntry?.timestamp || 0
      const bLast = bEntry?.timestamp || 0
      const aInteracted = Boolean(aEntry) || dmUserIds.has(a.userId)
      const bInteracted = Boolean(bEntry) || dmUserIds.has(b.userId)
      if (aInteracted !== bInteracted) return aInteracted ? -1 : 1
      if (aLast !== bLast) return bLast - aLast
      const aUnread = dmUnread[a.userId] || 0
      const bUnread = dmUnread[b.userId] || 0
      if (aUnread !== bUnread) return bUnread - aUnread
      return (a.bio || "").localeCompare(b.bio || "")
    })
  }, [currentUserId, dmLastMap, dmUnread, dmUserIds, instructorDirectory, mergedDMs])

  const memberDirectory = useMemo(() => {
    const map = new Map()
    mergedDMs.forEach((user) => {
      if (user?.userId) map.set(user.userId, user)
    })
    students.forEach((user) => {
      if (user?.userId) map.set(user.userId, user)
    })
    if (currentUserId && !map.has(currentUserId)) {
      map.set(currentUserId, {
        userId: currentUserId,
        bio: currentUserName,
        profilePic: auth.currentUser?.photoURL || sampleImage,
      })
    }
    return map
  }, [mergedDMs, currentUserId, currentUserName, auth.currentUser])

  const formatCallTime = (timestampMs) => {
    if (!timestampMs) return 'Just now'
    return new Date(timestampMs).toLocaleString('en-US', {
      day: 'numeric',
      month: 'short',
      hour: 'numeric',
      minute: '2-digit',
    })
  }

  const formatChannelTime = (timestampMs) => {
    if (!timestampMs) return ''
    return new Date(timestampMs).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
    })
  }

  const getCallStatusLabel = (session) => {
    const normalized = String(session?.status || '').trim().toLowerCase()
    const neverAnswered = !(session?.startedAtMs > 0 || normalized === 'active')

    if (currentUserId === session?.calleeId) {
      if (normalized === 'declined' || (normalized === 'ended' && neverAnswered)) {
        return 'Missed call'
      }
    }

    if (normalized === 'ended' && currentUserId === session?.callerId && neverAnswered) {
      return 'Not answered'
    }
    if (normalized === 'declined' && currentUserId === session?.callerId) {
      return 'Declined'
    }
    if (normalized === 'ended') {
      return 'Ended'
    }
    if (normalized === 'ringing') {
      return 'Ringing'
    }
    if (normalized === 'active') {
      return 'In call'
    }
    return 'Call'
  }

  const getCallStatusTone = (label) => {
    if (label === 'Missed call') return 'danger'
    if (label === 'Not answered') return 'muted'
    return 'primary'
  }

  const getCallProfile = (userId) => callProfiles.get(userId)
  const isWorkspaceCallForUser = (session, userId) => {
    if (!session || !userId) return false
    if (session.callerId === userId || session.calleeId === userId) {
      return true
    }
    return Array.isArray(session.participants) && session.participants.includes(userId)
  }

  const loadCallProfiles = async (sessions) => {
    const otherPartyIds = []
    sessions.forEach((session) => {
      const participantIds = Array.isArray(session?.participants) ? session.participants.filter(Boolean) : []
      const primaryOtherPartyId = participantIds.find((id) => id !== currentUserId)
        || (session.callerId === currentUserId ? session.calleeId : session.callerId)
      if (primaryOtherPartyId && !otherPartyIds.includes(primaryOtherPartyId)) {
        otherPartyIds.push(primaryOtherPartyId)
      }
    })

    if (otherPartyIds.length === 0) {
      return new Map()
    }

    const resolvedProfiles = new Map()
    const batches = []
    for (let index = 0; index < otherPartyIds.length; index += 10) {
      batches.push(otherPartyIds.slice(index, Math.min(index + 10, otherPartyIds.length)))
    }

    for (const batch of batches) {
      const batchSnapshot = await getDocs(
        query(collection(firestoreDb, 'users'), where('userId', 'in', batch))
      )

      const unresolvedIds = new Set(batch)
      batchSnapshot.forEach((documentSnapshot) => {
        const data = documentSnapshot.data() || {}
        const resolvedId = firstNonEmpty(data.userId, documentSnapshot.id)
        if (!resolvedId) {
          return
        }
        unresolvedIds.delete(resolvedId)
        unresolvedIds.delete(documentSnapshot.id)
        resolvedProfiles.set(resolvedId, {
          userId: resolvedId,
          displayName: firstNonEmpty(
            data.bio,
            data.displayName,
            data.name,
            data.fullName,
            data.userName,
            resolvedId,
            'Student'
          ),
          course: firstNonEmpty(data.course, ''),
          avatarUrl: firstNonEmpty(data.profilePic, sampleImage),
          phone: firstNonEmpty(data.phone, data.phoneNumber, ''),
        })
      })

      for (const unresolvedId of unresolvedIds) {
        const documentSnapshot = await getDoc(doc(firestoreDb, 'users', unresolvedId))
        if (!documentSnapshot.exists()) {
          continue
        }
        const data = documentSnapshot.data() || {}
        const resolvedId = firstNonEmpty(data.userId, documentSnapshot.id)
        if (!resolvedId) {
          continue
        }
        resolvedProfiles.set(resolvedId, {
          userId: resolvedId,
          displayName: firstNonEmpty(
            data.bio,
            data.displayName,
            data.name,
            data.fullName,
            data.userName,
            resolvedId,
            'Student'
          ),
          course: firstNonEmpty(data.course, ''),
          avatarUrl: firstNonEmpty(data.profilePic, sampleImage),
          phone: firstNonEmpty(data.phone, data.phoneNumber, ''),
        })
      }
    }

    return resolvedProfiles
  }

  const resolveWorkspaceCallName = (sessionName, otherParty, otherPartyId) => {
    const matchedStudent = students.find((student) => {
      const studentId = firstNonEmpty(student?.userId, student?.id)
      return studentId && studentId === otherPartyId
    })
    const directoryName = firstNonEmpty(
      matchedStudent?.bio,
      matchedStudent?.displayName,
      matchedStudent?.name,
      matchedStudent?.fullName,
      matchedStudent?.userName
    )
    const profileName = firstNonEmpty(otherParty?.displayName, directoryName)
    if (profileName && profileName !== otherPartyId) {
      return profileName
    }

    const sessionNameCandidate = firstNonEmpty(sessionName)
    if (sessionNameCandidate && sessionNameCandidate !== otherPartyId) {
      return sessionNameCandidate
    }

    return profileName || 'Student'
  }

  const sanitizeWorkspaceCallRoomName = (value) => {
    return String(value || '')
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9-]/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '')
      .slice(0, 80)
  }

  const createWorkspaceCallRoom = async (roomName) => {
    if (!WORKSPACE_DAILY_API_KEY) {
      throw new Error('Missing Daily API key')
    }

    const headers = {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${WORKSPACE_DAILY_API_KEY}`,
    }

    const roomLookup = await fetch(`${WORKSPACE_DAILY_API_URL}/rooms/${roomName}`, { headers })
    if (roomLookup.ok) {
      return roomLookup.json()
    }

    if (roomLookup.status !== 404) {
      throw new Error('Failed to check room')
    }

    const roomCreate = await fetch(`${WORKSPACE_DAILY_API_URL}/rooms`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        name: roomName,
        properties: {
          enable_chat: true,
          enable_screenshare: true,
          exp: Math.floor(Date.now() / 1000) + 60 * 60 * 6,
        },
      }),
    })

    if (!roomCreate.ok) {
      throw new Error('Failed to create room')
    }

    return roomCreate.json()
  }

  const createWorkspaceCallFallbackSession = async ({
    otherPartyId,
    otherPartyName,
    otherPartyCourse,
    otherPartyAvatar,
    otherPartyPhone,
    isVideo,
  }) => {
    if (!auth.currentUser || !currentUserId || !otherPartyId) {
      throw new Error('Please sign in again.')
    }

    const callRef = doc(collection(firestoreDb, 'workspaceCallSessions'))
    const roomName = sanitizeWorkspaceCallRoomName(`workspace-${callRef.id}`)
    const room = await createWorkspaceCallRoom(roomName)
    const roomUrl = firstNonEmpty(room?.url, `${WORKSPACE_DAILY_DOMAIN}/${roomName}`)
    const nowMs = Date.now()

    const session = {
      callId: callRef.id,
      roomName,
      roomUrl,
      callerId: currentUserId,
      callerName: currentUserName || 'Student',
      callerAvatar: currentUserAvatar || sampleImage,
      callerCourse: currentUserCourse || '',
      callerPhone: '',
      calleeId: otherPartyId,
      calleeName: otherPartyName || 'Student',
      calleeAvatar: otherPartyAvatar || sampleImage,
      calleeCourse: otherPartyCourse || '',
      calleePhone: otherPartyPhone || '',
      callType: isVideo ? 'video' : 'audio',
      isVideo: Boolean(isVideo),
      status: 'ringing',
      createdAtMs: nowMs,
      updatedAtMs: nowMs,
      startedAtMs: 0,
      endedAtMs: 0,
      durationSeconds: 0,
      acceptedBy: '',
      endedBy: '',
      callerJoinUrl: roomUrl,
      participants: [currentUserId, otherPartyId].filter(Boolean),
    }

    await setDoc(callRef, session)
    return session
  }

  const startWorkspaceCall = async ({ otherPartyId, otherPartyName, otherPartyCourse = '', otherPartyAvatar = '', otherPartyPhone = '', isVideo = true }) => {
    if (!canUseWorkspace) {
      showWorkspaceLockedMessage()
      return
    }
    if (!currentUserId || !otherPartyId) {
      swal('Call unavailable', 'We could not start this call right now.', 'error')
      return
    }
    if (!auth.currentUser) {
      swal('Call unavailable', 'Please sign in again.', 'error')
      return
    }

    try {
      const token = await auth.currentUser.getIdToken(true)
      const response = await fetch(WORKSPACE_CALLS_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          action: 'start',
          calleeId: otherPartyId,
          isVideo,
        }),
      })

      const session = await response.json().catch(() => ({}))
      if (!response.ok) {
        throw new Error(session?.error || 'We could not start this call right now.')
      }

      const normalizedSession = {
        ...session,
        callId: firstNonEmpty(session.callId, ''),
        roomName: firstNonEmpty(session.roomName, sanitizeWorkspaceCallRoomName(`workspace-${session.callId || Date.now()}`)),
        roomUrl: firstNonEmpty(session.roomUrl, `${WORKSPACE_DAILY_DOMAIN}/${sanitizeWorkspaceCallRoomName(`workspace-${session.callId || Date.now()}`)}`),
        callerJoinUrl: firstNonEmpty(session.callerJoinUrl, session.roomUrl, `${WORKSPACE_DAILY_DOMAIN}/${sanitizeWorkspaceCallRoomName(`workspace-${session.callId || Date.now()}`)}`),
      }

      navigate('/video-call', {
        state: {
          myData: `dm-${[currentUserId, otherPartyId].filter(Boolean).sort().join('-')}`,
          startedBy: currentUserId,
          startedByName: currentUserName,
          channelName: otherPartyName || 'Direct message',
          otherUserId: otherPartyId,
          otherUserName: otherPartyName || 'Student',
          otherUserCourse: otherPartyCourse,
          otherUserAvatar: otherPartyAvatar || sampleImage,
          otherUserPhone: otherPartyPhone,
          isVideo: Boolean(normalizedSession.isVideo),
          startVideoOff: !Boolean(normalizedSession.isVideo),
          workspaceCallId: normalizedSession.callId || '',
          workspaceCallRoomName: normalizedSession.roomName || '',
          workspaceCallRoomUrl: normalizedSession.roomUrl || '',
          workspaceCallJoinUrl: normalizedSession.callerJoinUrl || '',
        },
      })
    } catch (error) {
      try {
        const fallbackSession = await createWorkspaceCallFallbackSession({
          otherPartyId,
          otherPartyName: otherPartyName || 'Student',
          otherPartyCourse,
          otherPartyAvatar: otherPartyAvatar || sampleImage,
          otherPartyPhone,
          isVideo,
        })

        navigate('/video-call', {
          state: {
            myData: `dm-${[currentUserId, otherPartyId].filter(Boolean).sort().join('-')}`,
            startedBy: currentUserId,
            startedByName: currentUserName,
            channelName: otherPartyName || 'Direct message',
            otherUserId: otherPartyId,
            otherUserName: otherPartyName || 'Student',
            otherUserCourse: otherPartyCourse,
            otherUserAvatar: otherPartyAvatar || sampleImage,
            otherUserPhone: otherPartyPhone,
            isVideo: Boolean(fallbackSession.isVideo),
            startVideoOff: !Boolean(fallbackSession.isVideo),
            workspaceCallId: fallbackSession.callId || '',
            workspaceCallRoomName: fallbackSession.roomName || '',
            workspaceCallRoomUrl: fallbackSession.roomUrl || '',
            workspaceCallJoinUrl: fallbackSession.callerJoinUrl || '',
          },
        })
      } catch (fallbackError) {
        swal('Call unavailable', fallbackError?.message || error?.message || 'We could not start this call right now.', 'error')
      }
    }
  }

  const reopenWorkspaceCall = async (session) => {
    if (!session) return
    const otherPartyId = currentUserId === session.callerId ? session.calleeId : session.callerId
    const otherParty = getCallProfile(otherPartyId)
    const otherName = resolveWorkspaceCallName(
      currentUserId === session.callerId ? session.calleeName : session.callerName,
      otherParty,
      otherPartyId
    )
    const otherCourse = firstNonEmpty(
      currentUserId === session.callerId ? session.calleeCourse : session.callerCourse,
      otherParty?.course,
      ''
    )
    const otherAvatar = firstNonEmpty(
      currentUserId === session.callerId ? session.calleeAvatar : session.callerAvatar,
      otherParty?.avatarUrl,
      sampleImage
    )
    const otherPhone = currentUserId === session.callerId ? session.calleePhone : session.callerPhone

    await startWorkspaceCall({
      otherPartyId,
      otherPartyName: otherName,
      otherPartyCourse: otherCourse,
      otherPartyAvatar: otherAvatar,
      otherPartyPhone: otherPhone,
      isVideo: Boolean(session.isVideo),
    })
  }

  const getChannelMemberIds = (channel) => {
    if (!channel) return []
    const ids = isDefaultChannel(channel)
      ? Array.from(memberDirectory.keys())
      : Array.isArray(channel.members)
      ? channel.members
      : []
    return Array.from(new Set(ids.filter(Boolean)))
  }

  const getChannelOnlineCount = (channel) => {
    const ids = getChannelMemberIds(channel)
    const total = ids.length
    const online = ids.filter((id) => statusMap[id]?.state === "online").length
    return { online, total }
  }

  const getTimestamp = (value) => {
    if (!value) return 0
    if (typeof value === "number") return value
    if (value?.toMillis) return value.toMillis()
    if (value?.seconds) return value.seconds * 1000
    return 0
  }

  const formatTimestamp = (value) => {
    const ts = getTimestamp(value)
    if (!ts) return ""
    return new Date(ts).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
    })
  }

  const formatAdmissionDate = (value) => {
    const ts = getTimestamp(value)
    if (!ts) return "Unknown"
    return new Date(ts).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })
  }

  const getMessageTimestamp = (msg) => {
    return getTimestamp(msg.timestamp) || msg.clientTimestamp || 0
  }

  const normalizeWorkspaceMessage = (id, value = {}, channelId = '') => {
    const senderId = firstNonEmpty(value.senderId, '')
    const receiverId = firstNonEmpty(value.receiverId, '')
    const text = firstNonEmpty(value.text, '')
    const clientTimestampMs = getTimestamp(value.clientTimestamp || value.timestamp)
    const serverTimestampMs = getTimestamp(value.serverTimestamp)
    const attachmentUrl = firstNonEmpty(value.attachmentUrl, '')
    const attachmentName = firstNonEmpty(value.attachmentName, '')
    const attachmentType = firstNonEmpty(value.attachmentType, '')
    const attachmentSizeBytes = Number(firstNonEmpty(value.attachmentSizeBytes, 0)) || 0

    const reactionsByUser = value.reactionsByUser || value.reactions || {}
    const reactionEntries = Object.values(reactionsByUser || {})
    const reactionTally = reactionEntries.reduce((accumulator, entry) => {
      const emoji = firstNonEmpty(entry?.emoji, '')
      if (!emoji) return accumulator
      accumulator[emoji] = (accumulator[emoji] || 0) + 1
      return accumulator
    }, {})
    const topReactionEmoji = Object.keys(reactionTally).sort((left, right) => reactionTally[right] - reactionTally[left])[0] || ''
    const myReactionEmoji = firstNonEmpty(reactionsByUser?.[currentUserId]?.emoji, '')
    const systemMessage = Boolean(value.systemMessage) || firstNonEmpty(value.type, '').toLowerCase() === 'system'

    return {
      id,
      messageId: firstNonEmpty(value.messageId, id, ''),
      channelId: firstNonEmpty(value.channelId, channelId, ''),
      senderId,
      receiverId,
      text,
      clientTimestampMs,
      serverTimestampMs,
      timestamp: firstNonEmpty(value.timestamp, 0),
      read: Boolean(value.read) || Boolean(value.isRead),
      isRead: Boolean(value.read) || Boolean(value.isRead),
      readAtMs: getTimestamp(value.readAt),
      mine: currentUserId === senderId,
      isMine: currentUserId === senderId,
      edited: Boolean(value.edited) || Boolean(value.isEdited),
      isEdited: Boolean(value.edited) || Boolean(value.isEdited),
      systemMessage,
      attachmentUrl,
      attachmentName,
      attachmentType,
      attachmentSizeBytes,
      reactionsByUser,
      reactions: reactionsByUser,
      reactionCount: reactionEntries.length,
      myReactionEmoji,
      topReactionEmoji,
      senderRole: firstNonEmpty(value.senderRole, ''),
      senderName: firstNonEmpty(value.senderName, ''),
      senderAvatar: firstNonEmpty(value.senderAvatar, ''),
      senderCourse: firstNonEmpty(value.senderCourse, ''),
      type: firstNonEmpty(value.type, 'text'),
    }
  }

  const getMessagePreview = (value) => {
    if (!value?.text) return ""
    const plain = getPlainTextFromHtml(value.text)
    return plain.replace(/\s+/g, " ").trim()
  }

  const getReactionConversationKey = (chat = selectedChat) => {
    if (!chat) return ""
    if (chat.type === "dm") {
      const ids = [currentUserId, chat.userId].filter(Boolean).sort()
      return ids.length === 2 ? `dm__${ids[0]}__${ids[1]}` : ""
    }
    return chat.channelId ? `channel__${chat.channelId}` : ""
  }

  const getReactionState = (message) => (
    reactionDocuments[message?.id]?.reactionsByUser ||
    message?.reactionsByUser ||
    message?.reactions ||
    {}
  )

  const getReactionSummary = (message) => {
    const entries = Object.values(getReactionState(message))
      .filter((entry) => entry?.emoji)

    const grouped = entries.reduce((accumulator, entry) => {
      const current = accumulator.get(entry.emoji) || {
        emoji: entry.emoji,
        count: 0,
        reactedByMe: false,
      }
      current.count += 1
      if (entry.userId === currentUserId) current.reactedByMe = true
      accumulator.set(entry.emoji, current)
      return accumulator
    }, new Map())

    return Array.from(grouped.values())
  }

  const getMessageViewCount = (message) => {
    if (!message || message.senderId !== currentUserId) return 0

    if (selectedChat?.type === "dm") {
      return message.isRead ? 1 : 0
    }

    if (selectedChat?.type !== "channel") return 0

    return 0
  }

  const getMessageViewLabel = (message) => {
    const count = getMessageViewCount(message)
    if (count === 0) return selectedChat?.type === "dm" ? "Sent" : "Not viewed yet"
    return `Seen by ${count}`
  }

  const patchLocalMessageReaction = (messageId, userId, nextValue) => {
    if (!messageId || !userId) return
    setReactionDocuments((previous) => {
      const currentDoc = previous[messageId] || {}
      const nextReactions = { ...(currentDoc.reactionsByUser || {}) }
      if (nextValue) {
        nextReactions[userId] = nextValue
      } else {
        delete nextReactions[userId]
      }
      return {
        ...previous,
        [messageId]: {
          ...currentDoc,
          reactionsByUser: nextReactions,
        },
      }
    })
  }

  const applyPendingReactionOverrides = (items) => {
    const nextDocs = { ...items }
    Object.entries(pendingReactionOverridesRef.current).forEach(([messageId, override]) => {
      if (!override?.userId) return
      const currentDoc = nextDocs[messageId] || {}
      const nextReactions = { ...(currentDoc.reactionsByUser || {}) }
      if (override.nextValue) {
        nextReactions[override.userId] = override.nextValue
      } else {
        delete nextReactions[override.userId]
      }
      nextDocs[messageId] = {
        ...currentDoc,
        reactionsByUser: nextReactions,
      }
    })
    return nextDocs
  }

  const reconcilePendingReactionOverrides = (items) => {
    setPendingReactionOverrides((previous) => {
      const next = { ...previous }
      Object.entries(items || {}).forEach(([messageId, reactionDoc]) => {
        const override = previous[messageId]
        if (!override?.userId) return
        const liveEmoji = reactionDoc?.reactionsByUser?.[override.userId]?.emoji || ""
        const pendingEmoji = override?.nextValue?.emoji || ""
        if (liveEmoji === pendingEmoji) {
          delete next[messageId]
        }
      })
      return next
    })
  }

  const clearLongPressTimer = () => {
    if (!longPressTimerRef.current) return
    window.clearTimeout(longPressTimerRef.current)
    longPressTimerRef.current = null
  }

  const openReactionPicker = (messageId) => {
    if (!messageId) return
    setActiveReactionPickerId(messageId)
  }

  const closeReactionPicker = (messageId) => {
    setActiveReactionPickerId((previous) => (previous === messageId ? "" : previous))
  }

  const scheduleReactionPicker = (messageId) => {
    clearLongPressTimer()
    longPressTimerRef.current = window.setTimeout(() => {
      setActiveReactionPickerId(messageId)
      longPressTimerRef.current = null
    }, 420)
  }

  const isInstructorMessage = (message) => {
    if (!message) return false
    if (String(message.senderRole || "").toLowerCase() === "instructor") return true
    return instructorDirectory.has(message.senderId)
  }

  const showWorkspaceLockedMessage = () => {
    swal(
      "Workspace locked",
      "Workspace messaging becomes available once your status changes to Student.",
      "info"
    )
  }

  const openWorkspaceStatusComposer = async () => {
    if (!canUseWorkspace) {
      showWorkspaceLockedMessage()
      return
    }
    setWorkspaceStatusViewerOpen(false)
    setWorkspaceStatusViewerUserId('')
    setWorkspaceStatusViewerInitialStatusId('')
    setWorkspaceStatusViewerStories([])
    setWorkspaceStatusViewerIndex(0)
    setWorkspaceStatusViewersOpen(false)
    setWorkspaceStatusViewersStatus(null)
    setWorkspaceStatusViewers([])
    setWorkspaceStatusViewersLoading(false)
    setWorkspaceStatusComposerOpen(true)
  }

  const closeWorkspaceStatusComposer = () => {
    setWorkspaceStatusComposerOpen(false)
    setWorkspaceStatusComposerCaption('')
    setWorkspaceStatusComposerColorIndex(0)
    setWorkspaceStatusComposerPublishing(false)
    setWorkspaceStatusComposerProgress(0)
    setWorkspaceStatusComposerMediaFile(null)
    setWorkspaceStatusComposerMediaType('')
    if (workspaceStatusComposerPreviewUrl.startsWith('blob:')) {
      window.URL.revokeObjectURL(workspaceStatusComposerPreviewUrl)
    }
    setWorkspaceStatusComposerPreviewUrl('')
    if (statusMediaInputRef.current) {
      statusMediaInputRef.current.value = ''
    }
  }

  const handleWorkspaceStatusMediaSelection = (event) => {
    const file = event.target.files && event.target.files[0]
    if (!file) {
      return
    }

    const mimeType = String(file.type || '').toLowerCase()
    const isVideo = mimeType.startsWith('video/')
    if (isVideo && file.size > MAX_STATUS_VIDEO_BYTES) {
      swal('Video too large', 'Please choose a video under 40MB.', 'warning')
      event.target.value = ''
      return
    }

    if (workspaceStatusComposerPreviewUrl.startsWith('blob:')) {
      window.URL.revokeObjectURL(workspaceStatusComposerPreviewUrl)
    }

    const previewUrl = window.URL.createObjectURL(file)
    setWorkspaceStatusComposerMediaFile(file)
    setWorkspaceStatusComposerMediaType(isVideo ? 'video' : 'image')
    setWorkspaceStatusComposerPreviewUrl(previewUrl)
  }

  const triggerWorkspaceStatusMediaPicker = () => {
    if (statusMediaInputRef.current) {
      statusMediaInputRef.current.click()
    }
  }

  const clearWorkspaceStatusMedia = () => {
    if (workspaceStatusComposerPreviewUrl.startsWith('blob:')) {
      window.URL.revokeObjectURL(workspaceStatusComposerPreviewUrl)
    }
    setWorkspaceStatusComposerMediaFile(null)
    setWorkspaceStatusComposerMediaType('')
    setWorkspaceStatusComposerPreviewUrl('')
    if (statusMediaInputRef.current) {
      statusMediaInputRef.current.value = ''
    }
  }

  const closeWorkspaceStatusViewer = () => {
    setWorkspaceStatusViewerOpen(false)
    setWorkspaceStatusViewerUserId('')
    setWorkspaceStatusViewerInitialStatusId('')
    setWorkspaceStatusViewerStories([])
    setWorkspaceStatusViewerIndex(0)
    setWorkspaceStatusViewerLoading(false)
    setWorkspaceStatusViewersOpen(false)
    setWorkspaceStatusViewersStatus(null)
    setWorkspaceStatusViewers([])
    setWorkspaceStatusViewersLoading(false)
    viewerViewedStoryIdsRef.current = new Set()
  }

  const openWorkspaceStatusViewer = (statusId, userId) => {
    if (!statusId || !userId) {
      return
    }
    closeWorkspaceStatusComposer()
    setWorkspaceStatusViewersOpen(false)
    setWorkspaceStatusViewersStatus(null)
    setWorkspaceStatusViewers([])
    setWorkspaceStatusViewersLoading(false)
    viewerViewedStoryIdsRef.current = new Set()
    setWorkspaceStatusViewerUserId(userId)
    setWorkspaceStatusViewerInitialStatusId(statusId)
    setWorkspaceStatusViewerOpen(true)
    setSelectedWorkspaceStatusId(statusId)
  }

  const goToPreviousWorkspaceStatus = () => {
    setWorkspaceStatusViewerIndex((value) => Math.max(0, value - 1))
  }

  const goToNextWorkspaceStatus = () => {
    setWorkspaceStatusViewerIndex((value) => Math.min(Math.max(0, workspaceStatusViewerStories.length - 1), value + 1))
  }

  const openWorkspaceStatusViewers = async () => {
    const currentStory = workspaceStatusViewerStories[workspaceStatusViewerIndex]
    if (!currentStory || !currentStory.isMine) {
      return
    }

    setWorkspaceStatusViewersStatus(currentStory)
    setWorkspaceStatusViewersLoading(true)
    setWorkspaceStatusViewersOpen(true)
    setWorkspaceStatusViewers([])

    try {
      const viewersQuery = query(
        collection(firestoreDb, 'workspaceStatuses', currentStory.statusId, 'views'),
        orderBy('viewedAtMs', 'desc')
      )
      const snapshot = await getDocs(viewersQuery)
      setWorkspaceStatusViewers(
        snapshot.docs.map((documentSnapshot) => ({
          id: documentSnapshot.id,
          viewerId: firstNonEmpty(documentSnapshot.data()?.viewerId, documentSnapshot.id),
          displayName: firstNonEmpty(documentSnapshot.data()?.displayName, 'Student'),
          avatarUrl: firstNonEmpty(documentSnapshot.data()?.avatarUrl, sampleImage),
          course: firstNonEmpty(documentSnapshot.data()?.course, ''),
          viewedAtMs: getTimestamp(documentSnapshot.data()?.viewedAtMs),
        }))
      )
    } catch (error) {
      setWorkspaceStatusViewers([])
    } finally {
      setWorkspaceStatusViewersLoading(false)
    }
  }

  const closeWorkspaceStatusViewers = () => {
    setWorkspaceStatusViewersOpen(false)
    setWorkspaceStatusViewersStatus(null)
    setWorkspaceStatusViewers([])
    setWorkspaceStatusViewersLoading(false)
  }

  const publishWorkspaceStatus = async () => {
    const userId = currentUserId.trim()
    if (!userId) {
      return
    }

    const caption = workspaceStatusComposerCaption.trim()
    const hasCaption = caption.length > 0
    const hasMedia = Boolean(workspaceStatusComposerMediaFile)
    if (!hasCaption && !hasMedia) {
      swal('Empty status', 'Write a caption or add a photo/video first.', 'warning')
      return
    }

    const statusColor = normalizeStatusColor(STATUS_COLORS[workspaceStatusComposerColorIndex] || STATUS_COLORS[0])
    const nowMs = Date.now()
    const statusRef = doc(collection(firestoreDb, 'workspaceStatuses'))

    setWorkspaceStatusComposerPublishing(true)
    setWorkspaceStatusComposerProgress(0)

    try {
      const payload = {
        statusId: statusRef.id,
        userId,
        displayName: currentUserName || 'Student',
        course: currentUserCourse || '',
        avatarUrl: currentUserAvatar || sampleImage,
        caption,
        mediaUrl: '',
        mediaPath: '',
        mediaType: 'text',
        backgroundColor: statusColor,
        createdAtMs: nowMs,
        expiresAtMs: nowMs + STATUS_TTL_MS,
        viewerIds: [],
        viewCount: 0,
        lastViewedAtMs: 0,
      }

      if (workspaceStatusComposerMediaFile) {
        const mediaPath = `workspaceStatuses/${userId}/${nowMs}_${userId}.${getFileExtension(workspaceStatusComposerMediaFile)}`
        const uploadRef = storageRef(storage, mediaPath)
        const uploadTask = uploadBytesResumable(uploadRef, workspaceStatusComposerMediaFile)

        const mediaUrl = await new Promise((resolve, reject) => {
          uploadTask.on(
            'state_changed',
            (snapshot) => {
              const progress = snapshot.totalBytes
                ? Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100)
                : 0
              setWorkspaceStatusComposerProgress(progress)
            },
            reject,
            async () => {
              try {
                const url = await getDownloadURL(uploadTask.snapshot.ref)
                resolve(url)
              } catch (error) {
                reject(error)
              }
            }
          )
        })

        payload.mediaUrl = mediaUrl
        payload.mediaPath = mediaPath
        payload.mediaType = workspaceStatusComposerMediaType || 'image'
      }

      await setDoc(statusRef, payload)
      toast.success('Status posted')
      closeWorkspaceStatusComposer()
    } catch (error) {
      swal('Could not post status', 'We could not publish your update right now.', 'error')
    } finally {
      setWorkspaceStatusComposerPublishing(false)
      setWorkspaceStatusComposerProgress(0)
    }
  }

  const confirmAndDeleteWorkspaceStatus = () => {
    const currentStory = workspaceStatusViewerStories[workspaceStatusViewerIndex]
    if (!currentStory || !currentStory.isMine) {
      return
    }

    swal({
      title: 'Delete this status?',
      text: 'This will remove the story for everyone.',
      icon: 'warning',
      buttons: ['Cancel', 'Delete'],
      dangerMode: true,
    }).then(async (confirmed) => {
      if (!confirmed) {
        return
      }

      try {
        if (currentStory.mediaPath) {
          await deleteObject(storageRef(storage, currentStory.mediaPath))
        }
      } catch (error) {
        // Continue deleting the Firestore document even if media removal fails.
      }

      try {
        await deleteDoc(doc(firestoreDb, 'workspaceStatuses', currentStory.statusId))
        toast.success('Status deleted')
        closeWorkspaceStatusViewer()
      } catch (error) {
        swal('Delete failed', 'We could not delete that status.', 'error')
      }
    })
  }

  const openStatusCard = () => {
    const myStatus = workspaceStatusFeed.myStatus
    if (myStatus) {
      setSelectedWorkspaceStatusId(myStatus.statusId)
      openWorkspaceStatusViewer(myStatus.statusId, myStatus.userId)
      return
    }
    openWorkspaceStatusComposer()
  }

  const getWorkspaceStatusRelativeMeta = (story) => {
    if (!story) {
      return 'Just now'
    }
    const activeCount = Math.max(1, Number(story.activeCount || 1))
    const viewLabel =
      story.viewCount <= 0
        ? '0 views'
        : story.viewCount === 1
          ? '1 view'
          : `${story.viewCount} views`
    return `${formatWorkspaceStatusTime(story.createdAtMs)} • ${viewLabel}${activeCount > 1 ? ` • ${activeCount} updates` : ''}`
  }

  const renderWorkspaceStatusStory = (story, isPreview = false) => {
    if (!story) {
      return null
    }

    if (story.mediaType === 'image' && story.mediaUrl) {
      return (
        <img
          className={`workspaceStatusStoryMedia ${isPreview ? 'workspaceStatusStoryMediaPreview' : ''}`}
          src={story.mediaUrl}
          alt={story.caption || story.displayName}
        />
      )
    }

    if (story.mediaType === 'video' && story.mediaUrl) {
      return (
        <video
          className={`workspaceStatusStoryMedia ${isPreview ? 'workspaceStatusStoryMediaPreview' : ''}`}
          src={story.mediaUrl}
          controls
          autoPlay
          muted
          playsInline
        />
      )
    }

    return (
      <div
        className={`workspaceStatusTextStory ${isPreview ? 'workspaceStatusTextStoryPreview' : ''}`}
        style={{ background: story.backgroundColor || DEFAULT_TEXT_BACKGROUND }}
      >
        <div className="workspaceStatusTextStoryGlow" />
        <div className="workspaceStatusTextStoryBody">
          <span className="workspaceStatusTextStoryBadge">Status</span>
          <p>{story.caption || ' '}</p>
        </div>
      </div>
    )
  }

  const readChannelMembers = (channelSnapshot) => {
    const membersSnapshot = channelSnapshot.child('members')
    if (!membersSnapshot.exists()) {
      return []
    }

    const memberIds = []
    membersSnapshot.forEach((memberSnapshot) => {
      const memberId = memberSnapshot.key || ''
      if (!memberId) return
      const joined = memberSnapshot.child('joined').val()
      if (joined === false) return
      memberIds.push(memberId)
    })
    return memberIds
  }

  const toChannelTimestamp = (...values) => {
    for (const value of values) {
      if (value === null || value === undefined) continue
      const numeric = Number(value)
      if (Number.isFinite(numeric) && numeric > 0) {
        return numeric
      }
    }
    return 0
  }

  const buildGeneralChannelFallback = () => {
    const now = Date.now()
    return {
      id: 'general',
      channelId: 'general',
      name: 'General Channel',
      description: 'Everyone in Ubunifu College is here.',
      createdById: 'system',
      createdByName: 'Ubunifu College',
      createdByAvatar: '',
      createdAtMs: now,
      lastMessageAtMs: now,
      lastMessageText: 'Welcome to the General Channel.',
      lastSenderId: 'system',
      lastSenderName: 'Ubunifu College',
      isDefault: true,
      isPublic: true,
      memberCount: Math.max(1, memberDirectory.size || 1),
      unreadCount: 0,
      members: [],
      teamName: 'General Channel',
      teamDesc: 'Everyone in Ubunifu College is here.',
    }
  }

  const buildChannelSummary = (channelSnapshot) => {
    const channelId = firstNonEmpty(channelSnapshot.key)
    if (!channelId) {
      return null
    }

    const data = channelSnapshot.val() || {}
    const isDefault = Boolean(data.isDefault) || ['general', 'genral'].includes(channelId.trim().toLowerCase())
    const members = readChannelMembers(channelSnapshot)
    const memberMap = channelSnapshot.child('members')
    const currentMemberSnapshot = memberMap.child(currentUserId || '')
    const lastReadAt = toChannelTimestamp(
      currentMemberSnapshot.child('lastReadAt').val(),
      currentMemberSnapshot.child('lastSeenAt').val()
    )

    let lastMessageAtMs = toChannelTimestamp(data.lastMessageAt, data.lastMessageAtMs)
    let lastMessageText = firstNonEmpty(data.lastMessageText, data.lastMessage, '')
    let lastSenderId = firstNonEmpty(data.lastSenderId, '')
    let lastSenderName = firstNonEmpty(data.lastSenderName, '')
    let unreadCount = 0

    const messagesSnapshot = channelSnapshot.child('messages')
    if (messagesSnapshot.exists()) {
      messagesSnapshot.forEach((messageSnapshot) => {
        const clientTimestamp = toChannelTimestamp(
          messageSnapshot.child('clientTimestamp').val(),
          messageSnapshot.child('serverTimestamp').val()
        )
        const senderId = firstNonEmpty(messageSnapshot.child('senderId').val(), '')
        const senderName = firstNonEmpty(messageSnapshot.child('senderName').val(), '')
        const systemMessage =
          Boolean(messageSnapshot.child('systemMessage').val()) ||
          firstNonEmpty(messageSnapshot.child('type').val(), '').toLowerCase() === 'system'
        const attachmentType = firstNonEmpty(messageSnapshot.child('attachmentType').val(), '')
        const attachmentName = firstNonEmpty(messageSnapshot.child('attachmentName').val(), '')
        const text = firstNonEmpty(messageSnapshot.child('text').val(), '')

        if (clientTimestamp >= lastMessageAtMs) {
          lastMessageAtMs = clientTimestamp
          let preview = attachmentType.trim()
            ? attachmentType.toLowerCase() === 'image'
              ? 'Photo'
              : attachmentType.toLowerCase() === 'video'
                ? 'Video'
                : attachmentType.toLowerCase() === 'audio'
                  ? 'Audio'
                  : firstNonEmpty(attachmentName, text)
            : text
          if (Boolean(messageSnapshot.child('edited').val())) {
            preview = `${preview} (edited)`
          }
          if (senderId === currentUserId) {
            preview = `You: ${preview}`
          } else if (!systemMessage && senderName) {
            preview = `${senderName}: ${preview}`
          }
          lastMessageText = preview
          lastSenderId = senderId
          lastSenderName = senderName
        }

        if (!systemMessage && senderId !== currentUserId && clientTimestamp > lastReadAt) {
          unreadCount += 1
        }
      })
    }

    const creatorProfile = memberDirectory.get(firstNonEmpty(data.createdById, data.createdBy))
    const createdByName = firstNonEmpty(
      data.createdByName,
      creatorProfile?.bio,
      creatorProfile?.displayName,
      creatorProfile?.name,
      creatorProfile?.fullName,
      creatorProfile?.userName,
      'Student'
    )
    const createdByAvatar = firstNonEmpty(data.createdByAvatar, creatorProfile?.profilePic, sampleImage)
    const name = firstNonEmpty(data.name, data.teamName, isDefault ? 'General Channel' : 'Channel')
    const description = firstNonEmpty(
      data.description,
      data.teamDesc,
      isDefault ? 'Everyone in Ubunifu College is here.' : 'Group conversation'
    )
    const memberCount = Math.max(
      Number(data.memberCount || 0),
      members.length,
      isDefault ? Math.max(1, memberDirectory.size || 1) : 0
    )

    return {
      id: channelId,
      channelId,
      name,
      description,
      createdById: firstNonEmpty(data.createdById, data.createdBy, ''),
      createdByName,
      createdByAvatar,
      createdAtMs: toChannelTimestamp(data.createdAt, data.createdAtMs),
      lastMessageAtMs,
      lastMessageText: firstNonEmpty(lastMessageText, description),
      lastSenderId,
      lastSenderName,
      isDefault,
      isPublic: Boolean(data.isPublic) || isDefault,
      memberCount,
      unreadCount,
      members,
      teamName: name,
      teamDesc: description,
    }
  }

  const resolveWorkspaceStatusProfile = (userId, fallback = {}) => {
    const profile = memberDirectory.get(userId) || {}
    return {
      displayName: firstNonEmpty(
        fallback.displayName,
        profile.bio,
        profile.displayName,
        profile.name,
        profile.fullName,
        profile.userName,
        currentUserName,
        'Student'
      ),
      course: firstNonEmpty(fallback.course, profile.course, currentUserCourse, ''),
      avatarUrl: firstNonEmpty(fallback.avatarUrl, profile.profilePic, currentUserAvatar, sampleImage),
    }
  }

  const buildWorkspaceStatusStory = (documentSnapshot) => {
    const data = documentSnapshot.data() || {}
    const userId = firstNonEmpty(data.userId, documentSnapshot.id)
    const profile = resolveWorkspaceStatusProfile(userId, data)
    const viewerIds = Array.isArray(data.viewerIds) ? data.viewerIds : []
    const createdAtMs = Number(data.createdAtMs || 0)
    const expiresAtMs = Number(data.expiresAtMs || 0)

    return {
      statusId: firstNonEmpty(data.statusId, documentSnapshot.id),
      userId,
      displayName: profile.displayName,
      course: profile.course,
      avatarUrl: profile.avatarUrl,
      caption: firstNonEmpty(data.caption, ''),
      mediaUrl: firstNonEmpty(data.mediaUrl, ''),
      mediaPath: firstNonEmpty(data.mediaPath, ''),
      mediaType: firstNonEmpty(data.mediaType, data.mediaUrl ? 'image' : 'text'),
      backgroundColor: firstNonEmpty(data.backgroundColor, '#1F2937'),
      createdAtMs,
      expiresAtMs,
      viewCount: Number(data.viewCount || 0),
      activeCount: 1,
      isMine: userId === currentUserId,
      isViewedByMe: viewerIds.includes(currentUserId),
    }
  }

  const buildWorkspaceStatusFeed = (documents) => {
    const latestPerUser = new Map()
    let myStatus = null
    const now = Date.now()

    ;(documents || []).forEach((documentSnapshot) => {
      const story = buildWorkspaceStatusStory(documentSnapshot)
      if (story.expiresAtMs && story.expiresAtMs <= now) {
        return
      }

      if (story.isMine) {
        if (myStatus == null) {
          myStatus = story
        } else {
          myStatus = {
            ...myStatus,
            activeCount: myStatus.activeCount + 1,
          }
        }
        return
      }

      const existing = latestPerUser.get(story.userId)
      if (existing == null) {
        latestPerUser.set(story.userId, story)
        return
      }

      latestPerUser.set(story.userId, {
        ...existing,
        activeCount: existing.activeCount + 1,
      })
    })

    const recentStatuses = Array.from(latestPerUser.values()).sort(
      (left, right) => right.createdAtMs - left.createdAtMs
    )

    return {
      myStatus,
      recentStatuses,
    }
  }

  useEffect(() => {
    if (!currentUserId) {
      setChannels([])
      return undefined
    }

    const channelsRef = ref(realtimeDb, 'workspaceChannels')
    const unsubscribe = onValue(channelsRef, (snapshot) => {
      const nextChannels = []
      let hasGeneral = false

      snapshot.forEach((channelSnapshot) => {
        const summary = buildChannelSummary(channelSnapshot)
        if (!summary) return
        if (summary.channelId === 'general') {
          hasGeneral = true
        }
        nextChannels.push(summary)
      })

      if (!hasGeneral) {
        nextChannels.unshift(buildGeneralChannelFallback())
      }

      nextChannels.sort((left, right) => Number(right.lastMessageAtMs || 0) - Number(left.lastMessageAtMs || 0))
      setChannels(nextChannels)
    })

    return () => unsubscribe()
  }, [currentUserId, memberDirectory])

  useEffect(() => {
    if (!currentUserId) {
      setWorkspaceStatusFeed({ myStatus: null, recentStatuses: [] })
      setWorkspaceStatusLoading(false)
      setWorkspaceStatusError('')
      return undefined
    }

    setWorkspaceStatusLoading(true)
    setWorkspaceStatusError('')
    const feedQuery = query(
      collection(firestoreDb, 'workspaceStatuses'),
      orderBy('createdAtMs', 'desc'),
      limit(300)
    )

    const unsubscribe = onSnapshot(
      feedQuery,
      (snapshot) => {
        const nextFeed = buildWorkspaceStatusFeed(snapshot.docs)
        setWorkspaceStatusFeed(nextFeed)
        setSelectedWorkspaceStatusId((previous) => {
          if (previous && (nextFeed.myStatus?.statusId === previous || nextFeed.recentStatuses.some((story) => story.statusId === previous))) {
            return previous
          }
          return nextFeed.recentStatuses[0]?.statusId || nextFeed.myStatus?.statusId || ''
        })
        setWorkspaceStatusLoading(false)
      },
      () => {
        setWorkspaceStatusError('We could not load stories right now.')
        setWorkspaceStatusLoading(false)
      }
    )

    return () => unsubscribe()
  }, [currentUserId, memberDirectory])

  useEffect(() => {
    if (activeWorkspaceSection !== 'updates') {
      if (workspaceStatusComposerOpen) {
        closeWorkspaceStatusComposer()
      }
      if (workspaceStatusViewerOpen || workspaceStatusViewersOpen) {
        closeWorkspaceStatusViewer()
        closeWorkspaceStatusViewers()
      }
    }
  }, [activeWorkspaceSection])

  useEffect(() => {
    if (!workspaceStatusViewerOpen || !workspaceStatusViewerUserId || !currentUserId) {
      return undefined
    }

    setWorkspaceStatusViewerLoading(true)
    const viewerQuery = query(
      collection(firestoreDb, 'workspaceStatuses'),
      where('userId', '==', workspaceStatusViewerUserId)
    )

    const unsubscribe = onSnapshot(
      viewerQuery,
      (snapshot) => {
        const nextStories = snapshot.docs
          .map((documentSnapshot) => buildWorkspaceStatusStory(documentSnapshot))
          .filter((story) => !story.expiresAtMs || story.expiresAtMs > Date.now())
          .sort((left, right) => right.createdAtMs - left.createdAtMs)

        setWorkspaceStatusViewerStories(nextStories)
        setWorkspaceStatusViewerLoading(false)
        setWorkspaceStatusViewerIndex((previous) => {
          const initialIndex = nextStories.findIndex((story) => story.statusId === workspaceStatusViewerInitialStatusId)
          if (initialIndex >= 0) {
            return initialIndex
          }
          return Math.min(previous, Math.max(0, nextStories.length - 1))
        })
      },
      () => {
        setWorkspaceStatusViewerStories([])
        setWorkspaceStatusViewerLoading(false)
      }
    )

    return () => unsubscribe()
  }, [workspaceStatusViewerOpen, workspaceStatusViewerUserId, workspaceStatusViewerInitialStatusId, currentUserId, memberDirectory])

  useEffect(() => {
    if (!workspaceStatusViewerOpen || !currentUserId) {
      return
    }

    const currentStory = workspaceStatusViewerStories[workspaceStatusViewerIndex]
    if (!currentStory || currentStory.isMine || viewerViewedStoryIdsRef.current.has(currentStory.statusId)) {
      return
    }

    const viewerName = firstNonEmpty(currentUserName, 'Student')
    const viewerAvatar = firstNonEmpty(currentUserAvatar, sampleImage)
    const viewerCourse = firstNonEmpty(currentUserCourse, '')
    const statusRef = doc(firestoreDb, 'workspaceStatuses', currentStory.statusId)
    const viewerRef = doc(firestoreDb, 'workspaceStatuses', currentStory.statusId, 'views', currentUserId)

    runTransaction(firestoreDb, async (transaction) => {
      const viewerSnapshot = await transaction.get(viewerRef)
      if (viewerSnapshot.exists()) {
        return
      }

      transaction.update(statusRef, {
        viewerIds: arrayUnion(currentUserId),
        viewCount: increment(1),
        lastViewedAtMs: Date.now(),
      })

      transaction.set(viewerRef, {
        viewerId: currentUserId,
        displayName: viewerName,
        avatarUrl: viewerAvatar,
        course: viewerCourse,
        viewedAtMs: Date.now(),
      })
    })
      .then(() => {
        viewerViewedStoryIdsRef.current.add(currentStory.statusId)
      })
      .catch(() => {
        // If the update fails we just keep the story visible.
      })
  }, [workspaceStatusViewerOpen, workspaceStatusViewerStories, workspaceStatusViewerIndex, currentUserId, currentUserName, currentUserAvatar, currentUserCourse])

  useEffect(() => {
    const statusRef = ref(realtimeDb, 'status')
    const unsubscribe = onValue(statusRef, (snapshot) => {
      setStatusMap(snapshot.exists() ? snapshot.val() : {})
    })
    return () => unsubscribe()
  }, [realtimeDb])

  useEffect(() => {
    const fetchStudents = async () => {
      const q = query(collection(firestoreDb, 'users'), where('Status', '==', 'Active'))
      const querySnapshot = await getDocs(q)
      const items = querySnapshot.docs
        .map((doc) => ({ id: doc.id, ...doc.data() }))
        .filter((u) => u.userId !== auth.currentUser?.uid)
      setStudents(items)
    }

    fetchStudents()
  }, [])

  useEffect(() => {
    const fetchInstructors = async () => {
      const snapshot = await getDocs(collection(firestoreDb, 'Employees'))
      const directory = new Map()
      snapshot.docs.forEach((employeeDoc) => {
        const employee = employeeDoc.data()
        const employeeId = employee?.docId
        if (!employeeId) return
        directory.set(employeeId, {
          userId: employeeId,
          bio: employee?.employeeName || "Instructor",
          profilePic: employee?.profilePic || sampleImage,
        })
      })
      setInstructorDirectory(directory)
    }

    fetchInstructors()
  }, [])

  useEffect(() => {
    onAuthStateChanged(auth, (user) => {
      const nextUserId = user?.uid || ""
      setCurrentUserId(nextUserId)

      if (!nextUserId) {
        setDmLastMap({})
        setDmUsers([])
        return
      }

    })
  }, [])

  useEffect(() => {
    onAuthStateChanged(auth, async (user) => {
      if (!user) {
        setCurrentUserStatus("")
        setCurrentUserAvatar(sampleImage)
        setCurrentUserCourse("")
        return
      }
      const q = query(collection(firestoreDb, 'users'), where('userId', '==', user.uid))
      const snapshot = await getDocs(q)
      if (snapshot.empty) {
        setCurrentUserName(user.displayName || user.email || "Student")
        setCurrentUserAvatar(user.photoURL || sampleImage)
        setCurrentUserCourse("")
        setCurrentUserStatus("")
        return
      }
      snapshot.forEach((doc) => {
        setCurrentUserName(doc.data().bio || user.displayName || user.email || "Student")
        setCurrentUserAvatar(doc.data().profilePic || user.photoURL || sampleImage)
        setCurrentUserCourse(doc.data().course || "")
        setCurrentUserStatus(doc.data().userStatus || doc.data().Status || "")
      })
    })
  }, [])

  useEffect(() => {
    setSelectedChat(null)
    setMessages([])
    setNewMessage("")
    setSearchTerm("")
    setActiveWorkspaceSection("chats")
    setWorkspaceChatThreads([])
    setWorkspaceChatThreadsLoading(Boolean(currentUserId))
    setWorkspaceChatThreadsError("")
    setWorkspaceChatPresenceMap({})
    setWorkspaceChatProfileMap(new Map())
    setDmUsers([])
    setDmUnread({})
    setDmLastMap({})
    setChannelReadAt({})
    setChannelReadMap({})
    setChannelMessagesMap({})
    setChannelUnread({})
    setRecentCalls([])
    setCallsLoading(Boolean(currentUserId))
    setCallsError("")
    setCallProfiles(new Map())
    setWorkspaceStatusFeed({ myStatus: null, recentStatuses: [] })
    setWorkspaceStatusLoading(Boolean(currentUserId))
    setWorkspaceStatusError("")
    setSelectedWorkspaceStatusId("")
    setWorkspaceStatusComposerOpen(false)
    setWorkspaceStatusComposerCaption("")
    setWorkspaceStatusComposerColorIndex(0)
    setWorkspaceStatusComposerMediaFile(null)
    setWorkspaceStatusComposerMediaType("")
    if (workspaceStatusComposerPreviewUrl.startsWith('blob:')) {
      window.URL.revokeObjectURL(workspaceStatusComposerPreviewUrl)
    }
    setWorkspaceStatusComposerPreviewUrl("")
    setWorkspaceStatusComposerPublishing(false)
    setWorkspaceStatusComposerProgress(0)
    setWorkspaceStatusViewerOpen(false)
    setWorkspaceStatusViewerUserId("")
    setWorkspaceStatusViewerInitialStatusId("")
    setWorkspaceStatusViewerStories([])
    setWorkspaceStatusViewerIndex(0)
    setWorkspaceStatusViewerLoading(false)
    setWorkspaceStatusViewersOpen(false)
    setWorkspaceStatusViewersStatus(null)
    setWorkspaceStatusViewers([])
    setWorkspaceStatusViewersLoading(false)
    viewerViewedStoryIdsRef.current = new Set()
    setReactionDocuments({})
    setPendingReactionOverrides({})
    setActiveReactionPickerId("")
    clearSelectedAttachment()
    clearEditMode()
    setVoiceRecording(false)
    workspaceChatThreadsLoadedRef.current = false

    if (editorRef.current) {
      editorRef.current.innerHTML = ""
    }
  }, [currentUserId])

  useEffect(() => {
    if (canUseWorkspace) return
    setSelectedChat(null)
    setMessages([])
    setNewMessage("")
    clearSelectedAttachment()
    clearEditMode()
    setPendingDMTarget("")
    if (editorRef.current) {
      editorRef.current.innerHTML = ""
    }
  }, [canUseWorkspace])

  useEffect(() => {
    if (!currentUserId) {
      return undefined
    }

    setCallsLoading(true)
    setCallsError('')
    const callsQuery = query(
      collection(firestoreDb, 'workspaceCallSessions'),
      orderBy('createdAtMs', 'desc'),
      limit(50)
    )

    const unsubscribe = onSnapshot(
      callsQuery,
      (snapshot) => {
        const nextCalls = snapshot.docs
          .map((documentSnapshot) => ({
            callId: documentSnapshot.id,
            ...documentSnapshot.data(),
          }))
          .filter((session) => isWorkspaceCallForUser(session, currentUserId))
        setRecentCalls(nextCalls)
        setCallsLoading(false)
      },
      () => {
        setCallsError('We could not load your recent calls right now.')
        setCallsLoading(false)
      }
    )

    return () => unsubscribe()
  }, [currentUserId])

  useEffect(() => {
    if (!currentUserId || recentCalls.length === 0) {
      setCallProfiles(new Map())
      return undefined
    }

    let isMounted = true
    loadCallProfiles(recentCalls)
      .then((nextMap) => {
        if (isMounted) {
          setCallProfiles(nextMap)
        }
      })
      .catch(() => {
        if (isMounted) {
          setCallProfiles(new Map())
        }
      })

    return () => {
      isMounted = false
    }
  }, [currentUserId, recentCalls])

  useEffect(() => {
    if (!currentUserId || workspaceChatThreads.length === 0) {
      setWorkspaceChatProfileMap((previous) => (previous.size === 0 ? previous : new Map()))
      return undefined
    }

    const unresolvedIds = Array.from(new Set(
      workspaceChatThreads
        .map((thread) => thread.otherUserId)
        .filter(Boolean)
        .filter((userId) => {
          if (workspaceChatProfileMap.has(userId)) {
            return false
          }
          if (memberDirectory.has(userId) || instructorDirectory.has(userId)) {
            return false
          }
          return true
        })
    ))

    if (unresolvedIds.length === 0) {
      return undefined
    }

    let active = true
    const batches = []
    for (let index = 0; index < unresolvedIds.length; index += 10) {
      batches.push(unresolvedIds.slice(index, index + 10))
    }

    const loadProfiles = async () => {
      const resolved = new Map()
      for (const batch of batches) {
        const batchSnapshot = await getDocs(
          query(collection(firestoreDb, 'users'), where('userId', 'in', batch))
        )
        batchSnapshot.forEach((documentSnapshot) => {
          const data = documentSnapshot.data() || {}
          const resolvedId = firstNonEmpty(data.userId, documentSnapshot.id)
          if (!resolvedId) return
          resolved.set(resolvedId, {
            userId: resolvedId,
            bio: firstNonEmpty(data.bio, data.displayName, data.name, data.fullName, data.userName, 'Conversation'),
            displayName: firstNonEmpty(data.displayName, data.bio, data.name, data.fullName, data.userName, 'Conversation'),
            profilePic: firstNonEmpty(data.profilePic, data.avatarUrl, sampleImage),
            course: firstNonEmpty(data.course, ''),
            phone: firstNonEmpty(data.phone, data.phoneNumber, ''),
          })
        })

        const resolvedIds = new Set(Array.from(resolved.keys()))
        for (const unresolvedId of batch) {
          if (resolvedIds.has(unresolvedId)) {
            continue
          }
          const profileSnapshot = await getDoc(doc(firestoreDb, 'users', unresolvedId))
          if (!profileSnapshot.exists()) {
            continue
          }
          const data = profileSnapshot.data() || {}
          const resolvedId = firstNonEmpty(data.userId, profileSnapshot.id)
          if (!resolvedId) {
            continue
          }
          resolved.set(resolvedId, {
            userId: resolvedId,
            bio: firstNonEmpty(data.bio, data.displayName, data.name, data.fullName, data.userName, 'Conversation'),
            displayName: firstDisplayableName(data.displayName, data.bio, data.name, data.fullName, data.userName),
            profilePic: firstNonEmpty(data.profilePic, data.avatarUrl, sampleImage),
            course: firstNonEmpty(data.course, ''),
            phone: firstNonEmpty(data.phone, data.phoneNumber, ''),
          })
        }
      }

      if (!active || resolved.size === 0) {
        return
      }

      setWorkspaceChatProfileMap((previous) => {
        const next = new Map(previous)
        resolved.forEach((value, key) => {
          next.set(key, value)
        })
        return next
      })
    }

    void loadProfiles()

    return () => {
      active = false
    }
  }, [currentUserId, workspaceChatThreads, memberDirectory, instructorDirectory, workspaceChatProfileMap])

  useEffect(() => {
    if (!currentUserId) {
      setDmUsers([])
      setDmUnread({})
      setDmLastMap({})
      setWorkspaceChatThreads([])
      setWorkspaceChatThreadsLoading(false)
      setWorkspaceChatThreadsError('')
      setWorkspaceChatPresenceMap({})
      setWorkspaceChatProfileMap(new Map())
      workspaceChatThreadsLoadedRef.current = false
      return undefined
    }

    if (!workspaceChatThreadsLoadedRef.current) {
      setWorkspaceChatThreadsLoading(true)
    }
    setWorkspaceChatThreadsError('')

    const chatsRef = ref(realtimeDb, 'workspaceChats')
    const presenceRef = ref(realtimeDb, 'workspacePresence')

    const unsubscribeChats = onValue(
      chatsRef,
      (snapshot) => {
        if (!snapshot.exists()) {
          setWorkspaceChatThreads([])
          setDmUsers([])
          setDmUnread({})
          setDmLastMap({})
          setWorkspaceChatThreadsLoading(false)
          setWorkspaceChatThreadsError('')
          return
        }

        const dedupedThreads = new Map()
        const nextDmUsers = []
        const nextUnreadMap = {}
        const nextLastMap = {}

        snapshot.forEach((chatSnapshot) => {
          const thread = buildWorkspaceChatThreadSummary(chatSnapshot)
          if (!thread) {
            return
          }

          const currentThread = dedupedThreads.get(thread.otherUserId)
          if (!currentThread || thread.timestampMs >= currentThread.timestampMs) {
            dedupedThreads.set(thread.otherUserId, thread)
          }

          nextUnreadMap[thread.otherUserId] = thread.unreadCount
          nextLastMap[thread.otherUserId] = {
            text: firstNonEmpty(thread.preview, '').replace(/^You:\s*/i, ''),
            timestamp: thread.timestampMs,
            senderId: thread.lastMessageMine ? currentUserId : thread.otherUserId,
          }
          nextDmUsers.push({
            userId: thread.otherUserId,
            bio: thread.displayName,
            profilePic: thread.avatarUrl || sampleImage,
            course: thread.course || '',
            phone: thread.phone || '',
          })
        })

        const nextThreads = Array.from(dedupedThreads.values()).sort(
          (left, right) => right.timestampMs - left.timestampMs
        )

        setWorkspaceChatThreads(nextThreads)
        setDmUsers(nextDmUsers)
        setDmUnread(nextUnreadMap)
        setDmLastMap(nextLastMap)
        workspaceChatThreadsLoadedRef.current = true
        setWorkspaceChatThreadsLoading(false)
        setWorkspaceChatThreadsError('')
      },
      () => {
        setWorkspaceChatThreads([])
        setDmUsers([])
        setDmUnread({})
        setDmLastMap({})
        workspaceChatThreadsLoadedRef.current = true
        setWorkspaceChatThreadsLoading(false)
        setWorkspaceChatThreadsError('We could not load your workspace chats right now.')
      }
    )

    const unsubscribePresence = onValue(
      presenceRef,
      (snapshot) => {
        const nextPresenceMap = {}
        if (snapshot.exists()) {
          snapshot.forEach((presenceSnapshot) => {
            const userId = firstNonEmpty(presenceSnapshot.key, '')
            if (!userId) {
              return
            }
            nextPresenceMap[userId] = Boolean(presenceSnapshot.child('online').val()) || Boolean(presenceSnapshot.child('typing').val())
          })
        }
        setWorkspaceChatPresenceMap(nextPresenceMap)
      },
      () => {
        setWorkspaceChatPresenceMap({})
      }
    )

    return () => {
      unsubscribeChats()
      unsubscribePresence()
    }
  }, [currentUserId, realtimeDb, workspaceChatProfileMap])

  useEffect(() => {
    if (!debugDM) return
    console.log("[DM DEBUG] enabled")
    console.log("[DM DEBUG] currentUserId", currentUserId)
    console.log("[DM DEBUG] dmUsers", dmUsers.map((user) => ({
      userId: user.userId,
      name: user.bio,
    })))
    console.log("[DM DEBUG] dmLastMap", dmLastMap)
    console.log("[DM DEBUG] dmUnread", dmUnread)
    console.log("[DM DEBUG] sortedDMs", sortedDMs.map((user) => ({
      userId: user.userId,
      name: user.bio,
      last: dmLastMap[user.userId]?.timestamp || null,
      hasLastEntry: Boolean(dmLastMap[user.userId]),
      hasDmUser: dmUserIds.has(user.userId),
    })))
  }, [debugDM, currentUserId, dmUsers, dmLastMap, dmUnread, sortedDMs, dmUserIds])

  useEffect(() => {
    if (!currentUserId || channels.length === 0) return

    const unsubscribers = []
    channels.forEach((channel) => {
      const readRef = ref(realtimeDb, `workspaceChannels/${channel.id}/members/${currentUserId}`)
      const unsubscribeRead = onValue(readRef, (snapshot) => {
        const nextValue = snapshot.exists() ? snapshot.val() : {}
        setChannelReadMap((prev) => ({
          ...prev,
          [channel.id]: nextValue,
        }))
        setChannelReadAt((prev) => ({
          ...prev,
          [channel.id]: nextValue?.lastReadAt || 0,
        }))
      })
      unsubscribers.push(unsubscribeRead)

      const channelRef = ref(realtimeDb, `workspaceChannels/${channel.id}/messages`)
      const unsubscribeMessages = onValue(channelRef, (snapshot) => {
        const items = snapshot.exists()
          ? Object.entries(snapshot.val()).map(([id, value]) => normalizeWorkspaceMessage(id, value, channel.id))
          : []
        setChannelMessagesMap((prev) => ({
          ...prev,
          [channel.id]: items,
        }))
      })
      unsubscribers.push(unsubscribeMessages)
    })

    return () => {
      unsubscribers.forEach((unsubscribe) => {
        if (typeof unsubscribe === 'function') {
          unsubscribe()
        }
      })
    }
  }, [channels, currentUserId])

  useEffect(() => {
    const unreadMap = {}
    channels.forEach((channel) => {
      const msgs = channelMessagesMap[channel.id] || []
      const lastRead = channelReadAt[channel.id] || 0
      unreadMap[channel.id] = msgs.filter((msg) => {
        const ts = getMessageTimestamp(msg)
        return ts > lastRead && msg.senderId !== currentUserId && !msg.systemMessage
      }).length
    })
    setChannelUnread(unreadMap)
  }, [channels, channelMessagesMap, channelReadAt, currentUserId])

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" })
    }
  }, [messages])

  useEffect(() => {
    clearEditMode()
    clearSelectedAttachment()
    setActiveReactionPickerId("")
  }, [selectedChat?.type, selectedChat?.userId, selectedChat?.channelId])

  useEffect(() => () => clearLongPressTimer(), [])

  useEffect(() => () => {
    if (typingHandlerRef.current) {
      window.clearTimeout(typingHandlerRef.current)
      typingHandlerRef.current = null
    }
  }, [])

  useEffect(() => {
    pendingReactionOverridesRef.current = pendingReactionOverrides
  }, [pendingReactionOverrides])

  useEffect(() => {
    const conversationKey = getReactionConversationKey()
    if (!conversationKey) {
      setReactionDocuments({})
      return undefined
    }

    const reactionsRef = collection(firestoreDb, 'workspaceMessageReactions', conversationKey, 'entries')
    const unsubscribe = onSnapshot(reactionsRef, (snapshot) => {
      const nextDocs = {}
      snapshot.forEach((reactionDoc) => {
        const reaction = reactionDoc.data()
        if (!reaction?.messageId || !reaction?.userId) return
        if (!nextDocs[reaction.messageId]) {
          nextDocs[reaction.messageId] = { reactionsByUser: {} }
        }
        nextDocs[reaction.messageId].reactionsByUser[reaction.userId] = reaction
      })
      reconcilePendingReactionOverrides(nextDocs)
      setReactionDocuments(applyPendingReactionOverrides(nextDocs))
    })

    return () => unsubscribe()
  }, [selectedChat, currentUserId])

  useEffect(() => {
    if (!canUseWorkspace) {
      setMessages([])
      return
    }
    if (!selectedChat || !currentUserId) return

    let unsubscribeMessages = null

    if (selectedChat.type === 'dm') {
      const chatId = firstNonEmpty(selectedChat.chatId, getWorkspaceConversationId(currentUserId, selectedChat.userId))
      if (!chatId) {
        setMessages([])
        return
      }

      const chatRef = ref(realtimeDb, `workspaceChats/${chatId}/messages`)
      unsubscribeMessages = onValue(chatRef, (snapshot) => {
        if (!snapshot.exists()) {
          setMessages([])
          return
        }
        const formatted = Object.entries(snapshot.val()).map(([id, value]) => normalizeWorkspaceMessage(id, value))
        formatted.sort((a, b) => getMessageTimestamp(a) - getMessageTimestamp(b))
        setMessages(formatted)

        formatted.forEach((msg) => {
          if (msg.receiverId === currentUserId && !msg.isRead) {
            const msgRef = ref(realtimeDb, `workspaceChats/${chatId}/messages/${msg.id}`)
            update(msgRef, { read: true, isRead: true, readAt: realtimeTimestamp() })
          }
        })

        update(ref(realtimeDb, `workspaceChats/${chatId}`), {
          [`members/${currentUserId}/lastReadAt`]: Date.now(),
        })
      })
    }

    if (selectedChat.type === 'channel') {
      const channelRef = ref(realtimeDb, `workspaceChannels/${selectedChat.channelId}/messages`)
      unsubscribeMessages = onValue(channelRef, (snapshot) => {
        if (!snapshot.exists()) {
          setMessages([])
          return
        }
        const formatted = Object.entries(snapshot.val()).map(([id, value]) => normalizeWorkspaceMessage(id, value, selectedChat.channelId))
        formatted.sort((a, b) => getMessageTimestamp(a) - getMessageTimestamp(b))
        setMessages(formatted)
      })

      const readRef = ref(realtimeDb, `workspaceChannels/${selectedChat.channelId}/members/${currentUserId}`)
      update(readRef, {
        joined: true,
        lastReadAt: Date.now(),
        lastSeenAt: Date.now(),
      })
    }

    return () => {
      if (typeof unsubscribeMessages === 'function') {
        unsubscribeMessages()
      }
    }
  }, [selectedChat, currentUserId, canUseWorkspace])

  const selectChannel = (channel) => {
    if (!canUseWorkspace) return
    const isOpenChannel =
      isDefaultChannel(channel) ||
      channel.isPublic ||
      (Array.isArray(channel.members) && currentUserId && channel.members.includes(currentUserId))
    if (!isOpenChannel) {
      swal("Access denied", "You are not a member of this channel yet.", "warning")
      return
    }
    setSelectedChat({
      type: 'channel',
      channelId: channel.id,
      name: firstNonEmpty(channel.name, channel.teamName, 'Channel'),
      description: firstNonEmpty(channel.description, channel.teamDesc, 'Group conversation'),
    })
  }

  const selectDM = (user) => {
    if (!canUseWorkspace) return
    const resolvedProfile = resolveWorkspaceChatProfile(user?.userId)
    setActiveWorkspaceSection('chats')
    setSelectedChat({
      type: 'dm',
      chatId: getWorkspaceConversationId(currentUserId, user.userId),
      userId: user.userId,
      name: firstDisplayableName(user?.bio, resolvedProfile.displayName, 'Conversation'),
      avatar: firstNonEmpty(user?.profilePic, resolvedProfile.avatarUrl, sampleImage),
      course: firstNonEmpty(user?.course, resolvedProfile.course, ''),
      phone: firstNonEmpty(user?.phone, resolvedProfile.phone, ''),
    })
  }

  useEffect(() => {
    if (!canUseWorkspace) {
      setPendingDMTarget("")
      return
    }
    if (!pendingDMTarget) return

    const targetUser = mergedDMs.find((user) => user.userId === pendingDMTarget)
    if (!targetUser) {
      if (location.state?.openDMUserId && location.state?.openDMName) {
        const resolvedProfile = resolveWorkspaceChatProfile(location.state.openDMUserId)
        selectDM({
          userId: location.state.openDMUserId,
          bio: firstDisplayableName(location.state.openDMName, resolvedProfile.displayName, 'Conversation'),
          profilePic: firstNonEmpty(location.state.openDMAvatar, resolvedProfile.avatarUrl, sampleImage),
          course: firstNonEmpty(location.state.openDMCourse, resolvedProfile.course, ''),
          phone: firstNonEmpty(location.state.openDMPhone, resolvedProfile.phone, ''),
        })
        setPendingDMTarget("")
      }
      return
    }

    selectDM(targetUser)
    setPendingDMTarget("")
  }, [pendingDMTarget, mergedDMs, location.state, canUseWorkspace])

  const sendMessage = async () => {
    if (!canUseWorkspace) {
      showWorkspaceLockedMessage()
      return
    }
    const cleanHtml = sanitizeMessageHtml(newMessage)
    const plainText = getPlainTextFromHtml(cleanHtml).trim()
    if ((!plainText && !selectedAttachmentUri) || !selectedChat) {
      swal('Error', 'Message cannot be empty!', 'error')
      return
    }

    if (selectedChat.type === 'dm') {
      const localTimestamp = Date.now()
      const chatId = firstNonEmpty(selectedChat.chatId, getWorkspaceConversationId(currentUserId, selectedChat.userId))
      const messagesRef = chatId ? ref(realtimeDb, `workspaceChats/${chatId}/messages`) : null

      if (editingMessageId) {
        if (!chatId) {
          toast.error('We could not edit that message.')
          return
        }
        try {
          await update(ref(realtimeDb, `workspaceChats/${chatId}/messages/${editingMessageId}`), {
            text: cleanHtml,
            edited: true,
            isEdited: true,
            editedAt: localTimestamp,
            serverTimestamp: localTimestamp,
          })
          await rebuildWorkspaceConversationSummary(chatId, currentUserId, selectedChat.userId)
          clearEditMode()
          clearSelectedAttachment()
          updateMyPresence(false)
        } catch (error) {
          toast.error('We could not edit that message.')
        }
        return
      }

      const newMsgRef = messagesRef ? push(messagesRef) : null
      const messageData = {
        text: cleanHtml,
        senderId: currentUserId,
        receiverId: selectedChat.userId,
        timestamp: realtimeTimestamp(),
        clientTimestamp: localTimestamp,
        serverTimestamp: localTimestamp,
        isRead: false,
        read: false,
        readAt: 0,
        senderRole: "student",
        senderName: currentUserName || "Student",
        senderAvatar: currentUserAvatar || sampleImage,
        senderCourse: currentUserCourse || "",
        reactionsByUser: {},
        type: "text",
        attachmentUrl: selectedAttachmentUri ? String(selectedAttachmentUri) : "",
        attachmentName: selectedAttachmentName || "",
        attachmentType: selectedAttachmentType || "",
        attachmentSizeBytes: selectedAttachmentSizeBytes || 0,
        edited: false,
      }

      if (newMsgRef?.key && chatId) {
        const fullMessage = { ...messageData, messageId: newMsgRef.key, chatId }
        const updates = {
          [`workspaceChats/${chatId}/participants/${currentUserId}`]: true,
          [`workspaceChats/${chatId}/participants/${selectedChat.userId}`]: true,
          [`workspaceChats/${chatId}/members/${currentUserId}/lastActiveAt`]: localTimestamp,
          [`workspaceChats/${chatId}/members/${selectedChat.userId}/lastActiveAt`]: localTimestamp,
          [`workspaceChats/${chatId}/summary/lastMessageText`]: cleanHtml || getWorkspaceChatMessagePreview(fullMessage),
          [`workspaceChats/${chatId}/summary/lastMessageTimestamp`]: localTimestamp,
          [`workspaceChats/${chatId}/summary/lastSenderId`]: currentUserId,
          [`workspaceChats/${chatId}/summary/lastSenderName`]: currentUserName || "Student",
          [`workspaceChats/${chatId}/summary/updatedAt`]: localTimestamp,
          [`workspaceChats/${chatId}/messages/${newMsgRef.key}`]: fullMessage,
        }
        await update(ref(realtimeDb), updates)
      } else if (messagesRef) {
        await push(messagesRef, messageData)
      }
    } else {
      const channelId = selectedChat.channelId
      const channelMessagesRef = ref(realtimeDb, `workspaceChannels/${channelId}/messages`)
      const newMsgRef = push(channelMessagesRef)
      const messageData = {
        text: cleanHtml,
        senderId: currentUserId,
        senderName: currentUserName || "Student",
        senderAvatar: currentUserAvatar || sampleImage,
        senderCourse: currentUserCourse || "",
        senderRole: "student",
        timestamp: realtimeTimestamp(),
        clientTimestamp: Date.now(),
        reactionsByUser: {},
        reactions: {},
        channelId,
        systemMessage: false,
        edited: false,
        read: false,
        isRead: false,
        readAt: 0,
        attachmentUrl: selectedAttachmentUri ? String(selectedAttachmentUri) : "",
        attachmentName: selectedAttachmentName || "",
        attachmentType: selectedAttachmentType || "",
        attachmentSizeBytes: selectedAttachmentSizeBytes || 0,
      }
      if (newMsgRef?.key) {
        const updates = {
          [`workspaceChannels/${channelId}/members/${currentUserId}`]: {
            joined: true,
            lastReadAt: Date.now(),
            lastSeenAt: Date.now(),
          },
          [`workspaceChannels/${channelId}/lastMessageText`]: cleanHtml || getWorkspaceChatMessagePreview(messageData),
          [`workspaceChannels/${channelId}/lastMessageAt`]: Date.now(),
          [`workspaceChannels/${channelId}/lastSenderId`]: currentUserId,
          [`workspaceChannels/${channelId}/lastSenderName`]: currentUserName || "Student",
          [`workspaceChannels/${channelId}/messages/${newMsgRef.key}`]: { ...messageData, messageId: newMsgRef.key },
        }
        await update(ref(realtimeDb), updates)
      }
    }

    setNewMessage('')
    if (editorRef.current) {
      editorRef.current.innerHTML = ""
    }
    clearSelectedAttachment()
    clearEditMode()
  }

  const openCreateChannel = async () => {
    if (!canUseWorkspace) {
      showWorkspaceLockedMessage()
      return
    }
    const name = await swal({
      text: "Channel name",
      content: "input",
      button: {
        text: "Next",
        closeModal: true,
      },
    })

    if (!name) return
    if (DEFAULT_CHANNEL_NAMES.includes(name.trim().toLowerCase())) {
      swal("Not allowed", "That channel name is reserved for the default channel.", "warning")
      return
    }

    const desc = await swal({
      text: "Channel description",
      content: "input",
      button: {
        text: "Create channel",
        closeModal: true,
      },
    })

    try {
      const newChannelRef = push(ref(realtimeDb, 'workspaceChannels'))
      if (!newChannelRef.key) {
        throw new Error('We could not create that channel.')
      }

      const now = Date.now()
      const payload = {
        channelId: newChannelRef.key,
        name: name.trim(),
        description: (desc || 'Channel discussion').trim(),
        createdById: currentUserId || '',
        createdByName: currentUserName || 'Student',
        createdByAvatar: auth.currentUser?.photoURL || '',
        createdAt: now,
        lastMessageText: '',
        lastMessageAt: 0,
        lastSenderId: '',
        lastSenderName: '',
        isDefault: false,
        isPublic: false,
        memberCount: 1,
        teamName: name.trim(),
        teamDesc: (desc || 'Channel discussion').trim(),
      }

      const updates = {
        [`workspaceChannels/${newChannelRef.key}`]: payload,
        [`workspaceChannels/${newChannelRef.key}/members/${currentUserId}`]: {
          joined: true,
          joinedAt: now,
          lastReadAt: now,
          lastSeenAt: now,
        },
      }

      await update(ref(realtimeDb), updates)
      setChannels((prev) => [
        ...prev,
        {
          id: newChannelRef.key,
          channelId: newChannelRef.key,
          name: name.trim(),
          description: (desc || 'Channel discussion').trim(),
          createdById: currentUserId || '',
          createdByName: currentUserName || 'Student',
          createdByAvatar: auth.currentUser?.photoURL || '',
          createdAtMs: now,
          lastMessageAtMs: 0,
          lastMessageText: '',
          lastSenderId: '',
          lastSenderName: '',
          isDefault: false,
          isPublic: false,
          memberCount: 1,
          unreadCount: 0,
          members: [currentUserId].filter(Boolean),
          teamName: name.trim(),
          teamDesc: (desc || 'Channel discussion').trim(),
        },
      ])
      swal("Success", "Channel created", "success")
    } catch (error) {
      swal("Error", "Failed to create channel", "error")
    }
  }

  const openStudentPicker = () => {
    if (!canUseWorkspace) {
      showWorkspaceLockedMessage()
      return
    }
    setSelectedChat(null)
    setMessages([])
    setNewMessage('')
    setSearchTerm('')
    setActiveWorkspaceSection('contacts')
  }

  const addMemberToChannel = async () => {
    if (!canUseWorkspace) {
      showWorkspaceLockedMessage()
      return
    }
    if (!selectedChat || selectedChat.type !== "channel") return
    const channelData = channels.find((c) => c.id === selectedChat.channelId)
    const existingMembers = new Set(Array.isArray(channelData?.members) ? channelData.members : [])
    let availableStudents = students.filter((student) => student.userId && !existingMembers.has(student.userId))
    if (availableStudents.length === 0) {
      swal("No students", "No active students found.", "info")
      return
    }

    const container = document.createElement("div")
    container.className = "workspaceStudentModal"

    const searchInput = document.createElement("input")
    searchInput.className = "workspaceStudentSearch"
    searchInput.placeholder = "Search by name, course, or admission date"
    container.appendChild(searchInput)

    const list = document.createElement("div")
    list.className = "workspaceStudentList"
    container.appendChild(list)

    const renderList = (term = "") => {
      const query = term.trim().toLowerCase()
      list.innerHTML = ""
      const filtered = availableStudents.filter((student) => {
        const name = student.bio || ""
        const course = student.course || ""
        const admission = formatAdmissionDate(student.admissionDate || student.admissionDateTimestamp)
        const haystack = `${name} ${course} ${admission}`.toLowerCase()
        return !query || haystack.includes(query)
      })

      if (filtered.length === 0) {
        const empty = document.createElement("div")
        empty.className = "workspaceStudentEmpty"
        empty.textContent = "No students match your search."
        list.appendChild(empty)
        return
      }

      filtered.forEach((student) => {
        const row = document.createElement("div")
        row.className = "workspaceStudentRow"

        const img = document.createElement("img")
        img.src = student.profilePic || sampleImage
        img.alt = student.bio || "Student"

        const info = document.createElement("div")
        info.className = "workspaceStudentInfo"

        const name = document.createElement("div")
        name.className = "workspaceStudentName"
        name.textContent = student.bio || "Student"

        const course = document.createElement("div")
        course.className = "workspaceStudentMeta"
        course.textContent = student.course || "Course not set"

        const admission = document.createElement("div")
        admission.className = "workspaceStudentMeta"
        admission.textContent = `Admitted: ${formatAdmissionDate(student.admissionDate || student.admissionDateTimestamp)}`

        info.appendChild(name)
        info.appendChild(course)
        info.appendChild(admission)

        const action = document.createElement("button")
        action.className = "workspaceStudentAdd"
        action.textContent = "Add"
        action.addEventListener("click", async () => {
          action.disabled = true
          action.textContent = "Adding..."
          try {
            const now = Date.now()
            await update(ref(realtimeDb), {
              [`workspaceChannels/${selectedChat.channelId}/members/${student.userId}`]: {
                joined: true,
                joinedAt: now,
                lastReadAt: 0,
                lastSeenAt: 0,
              },
              [`workspaceChannels/${selectedChat.channelId}/memberCount`]: Math.max(
                1,
                Number(selectedChannelData?.memberCount || 0) + 1
              ),
            })
            setChannels((prev) =>
              prev.map((c) =>
                c.id === selectedChat.channelId
                  ? {
                      ...c,
                      members: Array.from(new Set([...(c.members || []), student.userId])),
                      memberCount: Math.max(1, Number(c.memberCount || 0) + 1),
                    }
                  : c
              )
            )
            availableStudents = availableStudents.filter((item) => item.userId !== student.userId)
            renderList(searchInput.value)
          } catch (error) {
            swal("Error", "Failed to add student.", "error")
            action.disabled = false
            action.textContent = "Add"
          }
        })

        row.appendChild(img)
        row.appendChild(info)
        row.appendChild(action)
        list.appendChild(row)
      })
    }

    searchInput.addEventListener("input", (event) => {
      renderList(event.target.value)
    })

    renderList("")

    swal({
      title: "Add students to channel",
      content: container,
      buttons: {
        cancel: {
          text: "Close",
          visible: true,
        },
      },
    })
  }

  const selectedChannelData =
    selectedChat?.type === "channel" ? channels.find((c) => c.id === selectedChat.channelId) : null
  const selectedChannelMembers = selectedChannelData
    ? getChannelMemberDisplay(selectedChannelData)
    : { members: [], remaining: 0, all: [] }

  const openMemberListModal = (channelOverride) => {
    const channelData = channelOverride || selectedChannelData
    if (!channelData) return
    const allMembers = getChannelMemberList(channelData)
    const container = document.createElement("div")
    container.className = "workspaceMemberModal"
    if (allMembers.length === 0) {
      const empty = document.createElement("div")
      empty.className = "workspaceMemberEmpty"
      empty.textContent = "No members yet."
      container.appendChild(empty)
    } else {
      allMembers.forEach((member) => {
        const row = document.createElement("div")
        row.className = "workspaceMemberRow"
        const img = document.createElement("img")
        img.src = member.avatar || sampleImage
        img.alt = member.name
        const name = document.createElement("span")
        name.textContent = member.name
        row.appendChild(img)
        row.appendChild(name)
        if (member.id === currentUserId) {
          const me = document.createElement("span")
          me.className = "workspaceMemberMe"
          me.textContent = "You"
          row.appendChild(me)
        }
        container.appendChild(row)
      })
    }

    swal({
      title: "Channel members",
      content: container,
      buttons: {
        confirm: {
          text: "Close",
          value: true,
        },
      },
    })
  }

  const openChannelInfoFromWorkspace = () => {
    if (!selectedChannelData) return

    const memberCount = selectedChannelMembers.all.length || selectedChannelData.memberCount || 0
    swal({
      title: selectedChannelData.name || 'Channel',
      text: `${selectedChannelData.description || 'Group conversation'}\n\n${memberCount} ${memberCount === 1 ? 'member' : 'members'}`,
      icon: 'info',
      buttons: {
        members: {
          text: 'Members',
          value: 'members',
        },
        close: {
          text: 'Close',
          value: null,
          visible: true,
        },
      },
    }).then((choice) => {
      if (choice === 'members') {
        openMemberListModal(selectedChannelData)
      }
    })
  }

  const startVideoCall = () => {
    if (!canUseWorkspace) {
      showWorkspaceLockedMessage()
      return
    }
    if (!selectedChat) return
    if (selectedChat.type === "dm") {
      const otherParty = memberDirectory.get(selectedChat.userId)
      void startWorkspaceCall({
        otherPartyId: selectedChat.userId,
        otherPartyName: selectedChat.name || otherParty?.bio || 'Direct message',
        otherPartyCourse: selectedChat.course || otherParty?.course || '',
        otherPartyAvatar: selectedChat.avatar || otherParty?.profilePic || sampleImage,
        otherPartyPhone: otherParty?.phone || '',
        isVideo: true,
      })
      return
    }
    navigate("/video-call", {
      state: {
        myData: `channel-${selectedChat.channelId}`,
        startedBy: currentUserId,
        startedByName: currentUserName,
        channelName: selectedChat.name || "Channel",
      },
    })
  }

  const toggleReaction = (message, emoji) => {
    if (!selectedChat || !currentUserId || !message?.id) return
    const currentReaction = getReactionState(message)?.[currentUserId]?.emoji || ""
    const nextValue = currentReaction === emoji
      ? null
      : {
          emoji,
          userId: currentUserId,
          name: currentUserName || "Student",
          reactedAt: Date.now(),
        }

    setPendingReactionOverrides((previous) => ({
      ...previous,
      [message.id]: {
        userId: currentUserId,
        nextValue,
      },
    }))
    patchLocalMessageReaction(message.id, currentUserId, nextValue)
    const conversationKey = getReactionConversationKey()
    if (!conversationKey) return
    const reactionDocRef = doc(firestoreDb, 'workspaceMessageReactions', conversationKey, 'entries', `${message.id}__${currentUserId}`)
    if (!nextValue) {
      deleteDoc(reactionDocRef)
      return
    }
    setDoc(reactionDocRef, {
      messageId: message.id,
      userId: currentUserId,
      ...nextValue,
      updatedAt: serverTimestamp()
    }, { merge: true })
  }

  const handleReactionPointerDown = (event, message, emoji) => {
    event.preventDefault()
    event.stopPropagation()
    if (selectedChat?.type === 'channel') {
      void toggleChannelReaction(message, emoji)
    } else {
      toggleReaction(message, emoji)
    }
    closeReactionPicker(message?.id || message?.messageId || '')
  }

  const workspaceSections = [
    {
      id: 'contacts',
      label: 'New chat',
      note: 'Start a conversation',
      icon: faUserPlus,
      actionLabel: 'Chats',
      action: () => setActiveWorkspaceSection('chats'),
    },
    {
      id: 'chats',
      label: 'Chats',
      note: 'Direct messages',
      icon: faCommentDots,
      actionLabel: 'New chat',
      action: openStudentPicker,
    },
    {
      id: 'updates',
      label: 'Updates',
      note: 'Statuses and stories',
      icon: faCircleDot,
      actionLabel: 'Add status',
      action: openWorkspaceStatusComposer,
    },
    {
      id: 'channels',
      label: 'Channels',
      note: 'Group rooms',
      icon: faHashtag,
      actionLabel: 'New channel',
      action: openCreateChannel,
    },
    {
      id: 'calls',
      label: 'Calls',
      note: 'Voice and video',
      icon: faPhone,
      actionLabel: 'Calls',
      action: () => setActiveWorkspaceSection('calls'),
    },
  ]

  const activeWorkspaceSectionData =
    workspaceSections.find((section) => section.id === activeWorkspaceSection) || workspaceSections[0]

  const workspaceListItems = useMemo(() => {
    if (activeWorkspaceSection === 'contacts') {
      const searchValue = searchTerm.trim().toLowerCase()
      return newChatContacts
        .filter((contact) => {
          if (!searchValue) return true
          const haystack = [
            contact.bio || '',
            contact.displayName || '',
            contact.name || '',
            contact.fullName || '',
            contact.course || '',
            contact.senderRole || '',
          ].join(' ').toLowerCase()
          return haystack.includes(searchValue)
        })
        .map((contact) => {
          const title = firstDisplayableName(
            contact.bio,
            contact.displayName,
            contact.name,
            contact.fullName,
            contact.userName,
            'Conversation'
          )
          return {
            id: contact.userId,
            kind: 'contact',
            title,
            subtitle: firstNonEmpty(contact.course, contact.senderRole, 'Tap to start chatting'),
            detail: '',
            badge: 0,
            avatar: contact.profilePic || sampleImage,
            active: selectedChat?.type === 'dm' && selectedChat.userId === contact.userId,
            online: Boolean(workspaceChatPresenceMap[contact.userId]),
            course: contact.course || '',
            phone: contact.phone || '',
            otherUserId: contact.userId,
            onClick: () =>
              selectDM({
                userId: contact.userId,
                bio: title,
                profilePic: contact.profilePic || sampleImage,
                course: contact.course || '',
                phone: contact.phone || '',
              }),
          }
        })
    }

    if (activeWorkspaceSection === 'channels') {
      return visibleChannels.map((channel) => {
        const preview = getChannelMemberPreview(channel)
        const presence = getChannelOnlineCount(channel)
        const channelName = firstNonEmpty(channel.name, channel.teamName, 'Channel')
        const channelDescription = firstNonEmpty(
          channel.description,
          channel.teamDesc,
          channel.isDefault ? 'Everyone in Ubunifu College is here.' : `${presence.online} online / ${presence.total} members`
        )
        return {
          id: channel.id,
          kind: 'channel',
          title: channelName,
          subtitle: channelDescription,
          detail: formatChannelTime(channel.lastMessageAtMs) || 'Recently',
          preview: firstNonEmpty(channel.lastMessageText, channelDescription),
          badge: channel.unreadCount || channelUnread[channel.id] || 0,
          avatar: preview.shown[0]?.avatar || channel.createdByAvatar || logo,
          active: selectedChat?.channelId === channel.id,
          time: channel.lastMessageAtMs,
          memberCount: presence.total || channel.memberCount || 0,
          createdByName: channel.createdByName,
          onClick: () => selectChannel(channel),
        }
      })
    }

    if (activeWorkspaceSection === 'updates') {
      const myStatus = workspaceStatusFeed.myStatus
      const recentStatuses = workspaceStatusFeed.recentStatuses
      const storyCards = []
      const searchValue = searchTerm.trim().toLowerCase()

      const myStatusText = [
        myStatus?.displayName || 'My status',
        myStatus?.course || '',
        myStatus?.caption || '',
        myStatus ? formatWorkspaceStatusTime(myStatus.createdAtMs) : '',
      ]
        .join(' ')
        .toLowerCase()

      if (!searchValue || myStatusText.includes(searchValue)) {
        storyCards.push({
          id: 'my-status',
          kind: 'status',
          title: 'My status',
          subtitle: myStatus
            ? `${formatWorkspaceStatusTime(myStatus.createdAtMs)} • ${myStatus.viewCount <= 0 ? '0 views' : myStatus.viewCount === 1 ? '1 view' : `${myStatus.viewCount} views`}`
            : 'Tap to add an update',
          detail: myStatus
            ? myStatus.activeCount > 1
              ? `${myStatus.activeCount} updates`
              : myStatus.isViewedByMe
                ? 'Seen'
                : 'New'
            : 'Photo, video, or text',
          badge: myStatus ? myStatus.activeCount || 0 : 0,
          badgeLabel: myStatus
            ? myStatus.activeCount > 1
              ? `${myStatus.activeCount} updates`
              : myStatus.isViewedByMe
                ? 'Seen'
                : 'New'
            : '',
          avatar: currentUserAvatar || sampleImage,
          active: Boolean(myStatus) && selectedWorkspaceStatusId === myStatus.statusId,
          onClick: openStatusCard,
        })
      }

      recentStatuses.forEach((story) => {
        const haystack = [
          story.displayName || '',
          story.course || '',
          story.caption || '',
          formatWorkspaceStatusTime(story.createdAtMs),
        ]
          .join(' ')
          .toLowerCase()
        if (searchValue && !haystack.includes(searchValue)) {
          return
        }
        storyCards.push({
          id: story.statusId,
          kind: 'status',
          title: story.displayName || 'Student',
          subtitle: story.course || story.caption || 'Status update',
          detail: formatWorkspaceStatusClock(story.createdAtMs) || 'Recently',
          badge: story.activeCount || 0,
          badgeLabel:
            story.activeCount > 1
              ? `${story.activeCount} updates`
              : story.isViewedByMe
                ? 'Seen'
                : 'New',
          avatar: story.avatarUrl || sampleImage,
          active: selectedWorkspaceStatusId === story.statusId,
          caption: story.caption || '',
          story,
          onClick: () => {
            setSelectedWorkspaceStatusId(story.statusId)
            openWorkspaceStatusViewer(story.statusId, story.userId)
          },
        })
      })

      return storyCards
    }

    if (activeWorkspaceSection === 'calls') {
      return recentCalls
        .filter((session) => {
          if (!searchTerm.trim()) return true
          const otherPartyId = currentUserId === session.callerId ? session.calleeId : session.callerId
          const otherParty = getCallProfile(otherPartyId)
          const haystack = [
            currentUserId === session.callerId ? session.calleeName : session.callerName,
            otherParty?.displayName || '',
            otherParty?.course || '',
            session.status || '',
            session.isVideo ? 'video' : 'audio',
          ].join(' ').toLowerCase()
          return haystack.includes(searchTerm.trim().toLowerCase())
        })
        .map((session) => {
          const otherPartyId = currentUserId === session.callerId ? session.calleeId : session.callerId
          const otherParty = getCallProfile(otherPartyId)
          const otherName = resolveWorkspaceCallName(
            currentUserId === session.callerId ? session.calleeName : session.callerName,
            otherParty,
            otherPartyId
          )
          const otherCourse = firstNonEmpty(
            currentUserId === session.callerId ? session.calleeCourse : session.callerCourse,
            otherParty?.course,
            ''
          )
          const otherAvatar = firstNonEmpty(
            currentUserId === session.callerId ? session.calleeAvatar : session.callerAvatar,
            otherParty?.avatarUrl,
            sampleImage
          )
          const statusLabel = getCallStatusLabel(session)
          const tone = getCallStatusTone(statusLabel)
          const callIds = [currentUserId, otherPartyId].filter(Boolean).sort()

          return {
            id: session.callId,
            kind: 'call',
            title: otherName,
            subtitle: `${session.isVideo ? 'Video call' : 'Audio call'}${otherCourse ? ` • ${otherCourse}` : ''}`,
            detail: formatCallTime(session.createdAtMs),
            badge: 0,
            avatar: otherAvatar,
            active: false,
            tone,
            statusLabel,
            session,
            onClick: () => {
              if (callIds.length !== 2) {
                swal('Call unavailable', 'We could not start this call right now.', 'error')
                return
              }

              void reopenWorkspaceCall(session)
            },
          }
        })
    }

    if (activeWorkspaceSection === 'chats') {
      const searchValue = searchTerm.trim().toLowerCase()
      return workspaceChatThreads
        .filter((thread) => {
          if (!searchValue) return true
          const haystack = [
            thread.displayName || '',
            thread.course || '',
            thread.preview || '',
            formatWorkspaceChatTime(thread.timestampMs) || '',
          ].join(' ').toLowerCase()
          return haystack.includes(searchValue)
        })
        .map((thread) => ({
          id: thread.chatId,
          kind: 'dm',
          title: thread.displayName || 'Conversation',
          subtitle: thread.preview || thread.course || 'Conversation',
          detail: formatWorkspaceChatTime(thread.timestampMs) || 'Recently',
          badge: thread.unreadCount || 0,
          avatar: thread.avatarUrl || sampleImage,
          active: selectedChat?.type === 'dm' && selectedChat.userId === thread.otherUserId,
          online: Boolean(workspaceChatPresenceMap[thread.otherUserId]),
          course: thread.course || '',
          phone: thread.phone || '',
          otherUserId: thread.otherUserId,
          onClick: () => selectDM({
            userId: thread.otherUserId,
            bio: thread.displayName || 'Conversation',
            profilePic: thread.avatarUrl || sampleImage,
            course: thread.course || '',
            phone: thread.phone || '',
          }),
        }))
    }

    return []
  }, [
    activeWorkspaceSection,
    channelUnread,
    callProfiles,
    currentUserId,
    dmLastMap,
    dmUnread,
    getChannelMemberPreview,
    getChannelOnlineCount,
    memberDirectory,
    workspaceChatPresenceMap,
    workspaceChatThreads,
    searchTerm,
    selectChannel,
    selectDM,
    sampleImage,
    navigate,
    recentCalls,
    students,
    visibleChannels,
    selectedChat,
  ])

  const selectedWorkspaceStatus = useMemo(() => {
    if (activeWorkspaceSection !== 'updates') {
      return null
    }
    return (
      workspaceStatusFeed.recentStatuses.find((story) => story.statusId === selectedWorkspaceStatusId)
        || workspaceStatusFeed.myStatus
        || workspaceStatusFeed.recentStatuses[0]
        || null
    )
  }, [
    activeWorkspaceSection,
    selectedWorkspaceStatusId,
    workspaceStatusFeed.myStatus,
    workspaceStatusFeed.recentStatuses,
  ])

  const currentWorkspaceStatusStory =
    workspaceStatusViewerStories[workspaceStatusViewerIndex]
    || selectedWorkspaceStatus
    || null

  const selectedDMProfile = useMemo(() => {
    if (!selectedChat || selectedChat.type !== 'dm') {
      return null
    }
    const profile = resolveWorkspaceChatProfile(selectedChat.userId)
    return {
      userId: selectedChat.userId,
      displayName: firstDisplayableName(selectedChat.name, profile.displayName, 'Conversation'),
      avatarUrl: firstNonEmpty(selectedChat.avatar, profile.avatarUrl, sampleImage),
      course: firstNonEmpty(selectedChat.course, profile.course, ''),
      phone: firstNonEmpty(selectedChat.phone, profile.phone, ''),
      online: Boolean(workspaceChatPresenceMap[selectedChat.userId]),
    }
  }, [
    sampleImage,
    selectedChat,
    workspaceChatProfileMap,
    workspaceChatPresenceMap,
    memberDirectory,
    instructorDirectory,
  ])

  return (
    <div className="workspaceShell workspaceShellWhatsAppLike">
      <aside className="workspaceSidebar workspaceSidebarRail">
        <div className="workspaceRailTop">
          <button className="workspaceRailBrand" onClick={() => navigate('/dashboard')} type="button" aria-label="Back to dashboard">
            <FontAwesomeIcon icon={faArrowLeft} />
          </button>
          <button className="workspaceRailCompose" onClick={openStudentPicker} disabled={!canUseWorkspace} type="button">
            <FontAwesomeIcon icon={faPlus} />
          </button>
        </div>

        <div className="workspaceRailTabs" role="tablist" aria-label="Workspace sections">
          {workspaceSections.filter((section) => section.id !== 'contacts').map((section) => {
            const active = activeWorkspaceSection === section.id
            return (
              <button
                key={section.id}
                type="button"
                className={active ? 'workspaceRailTab active' : 'workspaceRailTab'}
                onClick={() => {
                  if (section.id === 'updates') {
                    setActiveWorkspaceSection('updates')
                    return
                  }
                  if (section.id === 'calls') {
                    setActiveWorkspaceSection('calls')
                    return
                  }
                  setActiveWorkspaceSection(section.id)
                }}
                aria-pressed={active}
              >
                <span className="workspaceRailTabIcon">
                  <FontAwesomeIcon icon={section.icon} />
                </span>
                <span className="workspaceRailTabLabel">{section.label}</span>
              </button>
            )
          })}
        </div>

        <div className="workspaceRailFooter">
          <button className="workspaceRailFooterButton" onClick={() => setActiveWorkspaceSection('calls')} type="button">
            <FontAwesomeIcon icon={faVideo} />
          </button>
          <button className="workspaceRailFooterButton" onClick={() => navigate('/profile')} type="button">
            <FontAwesomeIcon icon={faGear} />
          </button>
        </div>
      </aside>

      <section className="workspaceMain workspaceMainListPane">
        <div className="workspaceListPane">
          <div className="workspaceListHeader">
            <div className="workspaceListHeading">
              <h2>Ubunifu Workspace</h2>
              <p>{canUseWorkspace ? activeWorkspaceSectionData.note : 'Read-only until your status becomes Student'}</p>
            </div>
            <div className="workspaceListHeaderActions">
              <button type="button" className="workspaceMiniAction" onClick={activeWorkspaceSectionData.action} disabled={!canUseWorkspace}>
                <FontAwesomeIcon icon={faPlus} />
              </button>
            </div>
          </div>

          <div className="workspaceSearch workspaceSearchLight">
            <FontAwesomeIcon icon={faSearch} />
              <input
              placeholder={
                activeWorkspaceSection === 'contacts'
                  ? 'Search people'
                  : activeWorkspaceSection === 'channels'
                    ? 'Search channels'
                  : activeWorkspaceSection === 'updates'
                    ? 'Search updates'
                    : activeWorkspaceSection === 'calls'
                      ? 'Search calls'
                      : 'Search or start a new chat'
              }
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              disabled={!canUseWorkspace && activeWorkspaceSection === 'chats'}
            />
          </div>

          <div
          className={
              activeWorkspaceSection === 'calls'
                ? 'workspaceCallsList'
                : activeWorkspaceSection === 'channels'
                  ? 'workspaceChannelsList'
                  : activeWorkspaceSection === 'updates'
                    ? 'workspaceStatusesList'
                    : activeWorkspaceSection === 'contacts'
                      ? 'workspaceChatsList'
                    : activeWorkspaceSection === 'chats'
                      ? 'workspaceChatsList'
                      : 'workspaceList workspaceListWhatsApp'
          }
          >
            {activeWorkspaceSection === 'contacts' && workspaceListItems.length === 0 && (
              <div className="workspaceChatsStateCard">
                <h3>No people found</h3>
                <p>Try again after the student and instructor lists finish loading.</p>
              </div>
            )}

            {activeWorkspaceSection === 'chats' && (
              <>
                {workspaceChatThreadsLoading && workspaceListItems.length === 0 && (
                  <div className="workspaceChatsStateCard">
                    <div className="workspaceCallsSpinner" />
                    <p>Loading chats…</p>
                  </div>
                )}

                {!workspaceChatThreadsLoading && workspaceChatThreadsError && workspaceListItems.length === 0 && (
                  <div className="workspaceChatsStateCard">
                    <h3>No chats yet</h3>
                    <p>{workspaceChatThreadsError}</p>
                  </div>
                )}

                {!workspaceChatThreadsLoading && !workspaceChatThreadsError && workspaceListItems.length === 0 && (
                  <div className="workspaceChatsStateCard">
                    <h3>No chats yet</h3>
                    <p>Start a conversation with a student, classmate, or instructor.</p>
                  </div>
                )}
              </>
            )}

            {activeWorkspaceSection === 'updates' && (
              <>
                {workspaceStatusLoading && (
                  <div className="workspaceStatusesStateCard">
                    <div className="workspaceCallsSpinner" />
                    <p>Loading stories…</p>
                  </div>
                )}

                {!workspaceStatusLoading && workspaceStatusError && workspaceListItems.length === 0 && (
                  <div className="workspaceStatusesStateCard">
                    <h3>No updates yet</h3>
                    <p>{workspaceStatusError}</p>
                  </div>
                )}

                {!workspaceStatusLoading && !workspaceStatusError && workspaceListItems.length === 0 && (
                  <div className="workspaceStatusesStateCard">
                    <h3>No updates yet</h3>
                    <p>Your stories will appear here once students start posting updates.</p>
                  </div>
                )}
              </>
            )}

            {activeWorkspaceSection === 'calls' && (
              <>
                {callsLoading && (
                  <div className="workspaceCallsStateCard">
                    <div className="workspaceCallsSpinner" />
                    <p>Loading recent calls…</p>
                  </div>
                )}

                {!callsLoading && callsError && workspaceListItems.length === 0 && (
                  <div className="workspaceCallsStateCard">
                    <h3>No recent calls</h3>
                    <p>{callsError}</p>
                  </div>
                )}

                {!callsLoading && !callsError && workspaceListItems.length === 0 && (
                  <div className="workspaceCallsStateCard">
                    <h3>No recent calls</h3>
                    <p>Your call history will appear here after you start or answer a call.</p>
                  </div>
                )}
              </>
            )}

            {activeWorkspaceSection === 'channels' && workspaceListItems.length === 0 && (
              <div className="workspaceChannelsStateCard">
                <h3>No channels yet</h3>
                <p>Channels will appear here once they are created in the workspace.</p>
              </div>
            )}

            {workspaceListItems.map((item) => {
              if (activeWorkspaceSection === 'calls') {
                return (
                  <button
                    key={`${activeWorkspaceSection}-${item.id}`}
                    type="button"
                    className="workspaceCallRow"
                    onClick={item.onClick}
                    disabled={!canUseWorkspace}
                  >
                    <div className={`workspaceCallRowAvatarShell workspaceCallRowAvatarShell--${item.tone || 'primary'}`}>
                      <img className="workspaceCallRowAvatar" src={item.avatar || sampleImage} alt="" />
                    </div>

                    <div className="workspaceCallRowCopy">
                      <strong>{item.title}</strong>
                      <span>{item.subtitle}</span>
                      <p>{item.statusLabel || item.detail}</p>
                    </div>

                    <div className="workspaceCallRowMeta">
                      <span className={`workspaceCallRowStatus workspaceCallRowStatus--${item.tone || 'primary'}`}>
                        {item.statusLabel || item.detail}
                      </span>
                      <time>{item.detail}</time>
                      <FontAwesomeIcon
                        icon={item.session?.isVideo ? faVideo : faPhone}
                        className={`workspaceCallRowIcon workspaceCallRowIcon--${item.tone || 'primary'}`}
                      />
                    </div>
                  </button>
                )
              }

              if (activeWorkspaceSection === 'contacts') {
                return (
                  <button
                    key={`${activeWorkspaceSection}-${item.id}`}
                    type="button"
                    className={item.active ? 'workspaceChatRow active' : 'workspaceChatRow'}
                    onClick={item.onClick}
                    disabled={!canUseWorkspace}
                  >
                    <div className="workspaceChatRowAvatarShell">
                      <img className="workspaceChatRowAvatar" src={item.avatar || sampleImage} alt="" />
                      {item.online && <span className="workspaceChatRowOnlineDot" />}
                    </div>

                    <div className="workspaceChatRowCopy">
                      <div className="workspaceChatRowTopline">
                        <strong>{item.title}</strong>
                      </div>
                      <p>{item.subtitle}</p>
                    </div>
                  </button>
                )
              }

              if (activeWorkspaceSection === 'channels') {
                return (
                  <button
                    key={`${activeWorkspaceSection}-${item.id}`}
                    type="button"
                    className={item.active ? 'workspaceChannelRow active' : 'workspaceChannelRow'}
                    onClick={item.onClick}
                    disabled={!canUseWorkspace}
                  >
                    <div className="workspaceChannelRowAvatarShell">
                      <span className="workspaceChannelRowInitials">{getInitials(item.title)}</span>
                    </div>

                    <div className="workspaceChannelRowCopy">
                      <div className="workspaceChannelRowTopline">
                        <strong>{item.title}</strong>
                        <time>{item.detail}</time>
                      </div>
                      <span className="workspaceChannelRowSubtitle">
                        {item.memberCount > 0
                          ? `${item.memberCount} ${item.memberCount === 1 ? 'member' : 'members'}`
                          : item.subtitle}
                      </span>
                      <p>{item.preview || item.subtitle}</p>
                    </div>

                    <div className="workspaceChannelRowMeta">
                      {item.badge > 0 && <span className="workspaceChannelRowBadge">{item.badge}</span>}
                    </div>
                  </button>
                )
              }

              if (activeWorkspaceSection === 'updates') {
                return (
                  <button
                    key={`${activeWorkspaceSection}-${item.id}`}
                    type="button"
                    className={item.active ? 'workspaceStatusRow active' : 'workspaceStatusRow'}
                    onClick={item.onClick}
                    disabled={!canUseWorkspace && activeWorkspaceSection === 'chats'}
                  >
                    <div className="workspaceStatusRowAvatarShell">
                      <img className="workspaceStatusRowAvatar" src={item.avatar || sampleImage} alt="" />
                    </div>

                    <div className="workspaceStatusRowCopy">
                      <strong>{item.title}</strong>
                      <span>{item.subtitle}</span>
                      <p>{item.caption || item.detail}</p>
                    </div>

                    <div className="workspaceStatusRowMeta">
                      <span className="workspaceStatusRowTime">{item.detail}</span>
                      <span className="workspaceStatusRowBadge">{item.badgeLabel || item.badge || 'New'}</span>
                    </div>
                  </button>
                )
              }

              if (activeWorkspaceSection === 'chats') {
                return (
                  <button
                    key={`${activeWorkspaceSection}-${item.id}`}
                    type="button"
                    className={item.active ? 'workspaceChatRow active' : 'workspaceChatRow'}
                    onClick={item.onClick}
                    disabled={!canUseWorkspace}
                  >
                    <div className="workspaceChatRowAvatarShell">
                      <img className="workspaceChatRowAvatar" src={item.avatar || sampleImage} alt="" />
                      {item.online && <span className="workspaceChatRowOnlineDot" />}
                    </div>

                    <div className="workspaceChatRowCopy">
                      <div className="workspaceChatRowTopline">
                        <strong>{item.title}</strong>
                        <time>{item.detail}</time>
                      </div>
                      <p>{item.subtitle}</p>
                    </div>

                    <div className="workspaceChatRowMeta">
                      {item.badge > 0 && (
                        <span className="workspaceChatRowBadge">{item.badge > 99 ? '99+' : item.badge}</span>
                      )}
                    </div>
                  </button>
                )
              }

              return (
                <button
                  key={`${activeWorkspaceSection}-${item.id}`}
                  type="button"
                  className={item.active ? 'workspaceListRow active' : 'workspaceListRow'}
                  onClick={item.onClick}
                  disabled={!canUseWorkspace && activeWorkspaceSection === 'chats'}
                >
                  <img className="workspaceListAvatar" src={item.avatar || sampleImage} alt="" />
                  <div className="workspaceListBody">
                    <div className="workspaceListTopline">
                      <strong>{item.title}</strong>
                      <span>{item.detail}</span>
                    </div>
                    <p>{item.subtitle}</p>
                  </div>
                  {item.badge > 0 && <span className="workspaceListBadge">{item.badge}</span>}
                </button>
              )
            })}

            {workspaceListItems.length === 0 && activeWorkspaceSection !== 'calls' && activeWorkspaceSection !== 'updates' && activeWorkspaceSection !== 'chats' && activeWorkspaceSection !== 'contacts' && (
              <div className="workspaceListEmpty">
                <h3>No items yet</h3>
                <p>Use the plus button to create a new chat or channel.</p>
              </div>
            )}
          </div>
        </div>
      </section>

      <section className="workspaceMain workspaceMainPreviewPane">
        <div className="workspacePreviewPane">
          {selectedChat?.type === 'channel' && selectedChannelData ? (
            <div className="workspaceConversationCard workspaceConversationCard--channel">
              <div className="workspaceConversationHeader">
                <button
                  type="button"
                  className="workspaceConversationHeaderBack"
                  onClick={() => {
                    setSelectedChat(null)
                    setActiveWorkspaceSection('channels')
                    clearEditMode()
                    clearSelectedAttachment()
                    setActiveReactionPickerId("")
                  }}
                  aria-label="Back to channels"
                >
                  <FontAwesomeIcon icon={faArrowLeft} />
                </button>

                <button
                  type="button"
                  className="workspaceConversationHeaderProfile workspaceConversationHeaderProfile--channel"
                  onClick={() => openMemberListModal(selectedChannelData)}
                >
                  <span>{getInitials(selectedChannelData.name)}</span>
                </button>

                <button
                  type="button"
                  className="workspaceConversationHeaderCopy"
                  onClick={() => openMemberListModal(selectedChannelData)}
                >
                  <strong>{selectedChannelData.name || 'Channel'}</strong>
                  <span>
                    {selectedChannelData.isDefault
                      ? 'Everyone in Ubunifu College is here.'
                      : `${selectedChannelMembers.all.length || selectedChannelData.memberCount || 0} members`}
                  </span>
                </button>

                <div className="workspaceConversationHeaderActions">
                  <button type="button" onClick={() => openMemberListModal(selectedChannelData)} disabled={!canUseWorkspace} aria-label="Channel members">
                    <FontAwesomeIcon icon={faUserPlus} />
                  </button>
                  <button type="button" onClick={() => void openChannelInfoFromWorkspace()} disabled={!canUseWorkspace} aria-label="Channel info">
                    <span>⋯</span>
                  </button>
                </div>
              </div>

              <div className="workspaceConversationBody">
                <div className="workspaceChatDateChip">
                  <span>{messages.length > 0 ? 'Today' : 'No messages yet'}</span>
                </div>

                {messages.length === 0 ? (
                  <div className="workspaceConversationEmpty">
                    <div className="workspaceConversationEmptyIcon">
                      <FontAwesomeIcon icon={faHashtag} />
                    </div>
                    <h3>No messages yet</h3>
                    <p>Start the conversation in this channel.</p>
                  </div>
                ) : (
                  <div className="workspaceConversationMessages" onClick={() => setActiveReactionPickerId('')}>
                    {messages.map((message, index) => {
                      const isMine = Boolean(message.isMine)
                      const messageId = firstNonEmpty(message.messageId, message.id, '')
                      const reactionSummary = getReactionSummary(message)
                      const isPickerOpen = activeReactionPickerId === messageId
                      const attachmentType = firstNonEmpty(message.attachmentType, '').toLowerCase()
                      const hasAttachment = Boolean(firstNonEmpty(message.attachmentUrl, ''))
                      const isSystem = Boolean(message.systemMessage)

                      return (
                        <div
                          key={messageId || `${message.senderId || 'message'}-${message.clientTimestamp || message.serverTimestamp || index}`}
                          className={
                            isSystem
                              ? 'workspaceConversationRow workspaceConversationRow--system'
                              : isMine
                                ? 'workspaceConversationRow workspaceConversationRow--mine'
                                : 'workspaceConversationRow workspaceConversationRow--incoming'
                          }
                          onContextMenu={(event) => {
                            event.preventDefault()
                            if (!isSystem) {
                              void openWorkspaceMessageActions(message)
                            }
                          }}
                          onPointerDown={() => {
                            if (!isSystem) {
                              scheduleReactionPicker(messageId)
                            }
                          }}
                          onPointerUp={clearLongPressTimer}
                          onPointerCancel={clearLongPressTimer}
                          onPointerLeave={clearLongPressTimer}
                        >
                          <div
                            className={
                              isSystem
                                ? 'workspaceConversationBubble workspaceConversationBubble--system'
                                : isMine
                                  ? 'workspaceConversationBubble workspaceConversationBubble--mine'
                                  : 'workspaceConversationBubble workspaceConversationBubble--incoming'
                            }
                          >
                            {!isMine && !isSystem && (
                              <strong className="workspaceConversationSender">
                                {message.senderName || 'Student'}
                              </strong>
                            )}

                            {hasAttachment && attachmentType === 'image' && (
                              <button
                                type="button"
                                className="workspaceConversationAttachmentImage"
                                onClick={() => openAttachmentPreview('image', message.attachmentUrl, message.attachmentName || 'Photo')}
                              >
                                <img src={message.attachmentUrl} alt={message.attachmentName || 'Attachment'} />
                              </button>
                            )}

                            {hasAttachment && attachmentType === 'video' && (
                              <button
                                type="button"
                                className="workspaceConversationAttachmentVideo"
                                onClick={() => openAttachmentPreview('video', message.attachmentUrl, message.attachmentName || 'Video')}
                              >
                                <span>▶</span>
                                <strong>{message.attachmentName || 'Video'}</strong>
                                <p>Tap to open</p>
                              </button>
                            )}

                            {hasAttachment && attachmentType === 'audio' && (
                              <button
                                type="button"
                                className="workspaceConversationAttachmentAudio"
                                onClick={() => openAudioUrl(message.attachmentUrl)}
                              >
                                <span>🎤</span>
                                <strong>{message.attachmentName || 'Voice message'}</strong>
                                <p>Tap to play</p>
                              </button>
                            )}

                            {hasAttachment && attachmentType === 'file' && (
                              <button
                                type="button"
                                className="workspaceConversationAttachmentFile"
                                onClick={() => openAttachmentUrl(message.attachmentUrl)}
                              >
                                <span>📄</span>
                                <strong>{message.attachmentName || 'File'}</strong>
                                <p>Tap to open</p>
                              </button>
                            )}

                            {message.text && (
                              <div
                                className="workspaceConversationBubbleText"
                                dangerouslySetInnerHTML={{ __html: message.text }}
                              />
                            )}

                            {!isSystem && reactionSummary.length > 0 && (
                              <div className="workspaceConversationReactions">
                                {reactionSummary.map((reaction) => (
                                  <button
                                    key={`${messageId}-${reaction.emoji}`}
                                    type="button"
                                    className={reaction.reactedByMe ? 'workspaceConversationReaction active' : 'workspaceConversationReaction'}
                                    onClick={(event) => handleReactionPointerDown(event, message, reaction.emoji)}
                                  >
                                    <span>{reaction.emoji}</span>
                                    <strong>{reaction.count}</strong>
                                  </button>
                                ))}
                              </div>
                            )}

                            {!isSystem && (
                              <div className="workspaceConversationFooter">
                                <time>{formatWorkspaceChatTime(getMessageTimestamp(message))}</time>
                                {!isMine && <span>{message.senderCourse || ''}</span>}
                                {message.isEdited && <span>Edited</span>}
                              </div>
                            )}

                            {!isSystem && isPickerOpen && (
                              <div className="workspaceConversationActionStrip">
                                {REACTION_OPTIONS.map((emoji) => (
                                  <button
                                    key={`${messageId}-${emoji}`}
                                    type="button"
                                    onClick={(event) => handleReactionPointerDown(event, message, emoji)}
                                  >
                                    {emoji}
                                  </button>
                                ))}
                                {!isSystem && (
                                  <button type="button" onClick={() => void openWorkspaceMessageActions(message)}>
                                    ⋯
                                  </button>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      )
                    })}
                    <div ref={messagesEndRef} />
                  </div>
                )}
              </div>

              <div className="workspaceConversationComposer">
                {editingMessageId && (
                  <div className="workspaceConversationEditBanner">
                    <strong>Editing message</strong>
                    <button type="button" onClick={clearEditMode}>Cancel</button>
                  </div>
                )}

                <div className="workspaceConversationComposerRow">
                  <button type="button" onClick={() => toast.info('Emoji picker will be wired into the composer next.')} aria-label="Emoji">
                    🙂
                  </button>
                  <button type="button" onClick={() => toast.info('Attachment upload will match the mobile flow next.')} aria-label="Attach">
                    📎
                  </button>
                  <div
                    className="workspaceConversationEditor"
                    ref={editorRef}
                    contentEditable={canUseWorkspace}
                    suppressContentEditableWarning
                    onInput={updateEditorState}
                    onBlur={updateEditorState}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' && !event.shiftKey) {
                        event.preventDefault()
                        void sendMessage()
                      }
                    }}
                    data-placeholder={editingMessageId ? 'Edit message' : 'Type a message'}
                  />
                  {newMessage.trim() || selectedAttachmentUri ? (
                    <button type="button" className="workspaceConversationSendButton" onClick={() => void sendMessage()} disabled={!canUseWorkspace}>
                      <FontAwesomeIcon icon={faPaperPlane} />
                    </button>
                  ) : (
                    <button type="button" className="workspaceConversationMicButton" onClick={() => void toggleVoiceRecording()} disabled={!canUseWorkspace}>
                      <FontAwesomeIcon icon={faMicrophone} />
                    </button>
                  )}
                </div>

                {selectedAttachmentUri && (
                  <div className="workspaceConversationAttachmentPreview">
                    <div>
                      <strong>{selectedAttachmentName || 'Attachment'}</strong>
                      <span>{selectedAttachmentType || 'file'}</span>
                    </div>
                    <button type="button" onClick={clearSelectedAttachment}>Remove</button>
                  </div>
                )}
              </div>
            </div>
          ) : selectedChat?.type === 'dm' && selectedDMProfile ? (
            <div className="workspaceConversationCard">
              <div className="workspaceConversationHeader">
                <button
                  type="button"
                  className="workspaceConversationHeaderBack"
                  onClick={() => {
                    setSelectedChat(null)
                    setActiveWorkspaceSection('chats')
                    clearEditMode()
                    clearSelectedAttachment()
                    setActiveReactionPickerId("")
                  }}
                  aria-label="Back to chats"
                >
                  <FontAwesomeIcon icon={faArrowLeft} />
                </button>

                <button
                  type="button"
                  className="workspaceConversationHeaderProfile"
                >
                  <img src={selectedDMProfile.avatarUrl || sampleImage} alt="" />
                </button>

                <button
                  type="button"
                  className="workspaceConversationHeaderCopy"
                >
                  <strong>{selectedDMProfile.displayName || 'Conversation'}</strong>
                  <span>
                    {selectedDMProfile.online
                      ? 'Online'
                      : selectedDMProfile.course || 'Direct message'}
                  </span>
                </button>

                <div className="workspaceConversationHeaderActions">
                  <button type="button" onClick={() => void startWorkspaceCall({
                    otherPartyId: selectedDMProfile.userId,
                    otherPartyName: selectedDMProfile.displayName,
                    otherPartyCourse: selectedDMProfile.course,
                    otherPartyAvatar: selectedDMProfile.avatarUrl,
                    otherPartyPhone: selectedDMProfile.phone,
                    isVideo: true,
                  })} disabled={!canUseWorkspace} aria-label="Video call">
                    <FontAwesomeIcon icon={faVideo} />
                  </button>
                  <button type="button" onClick={() => void startWorkspaceCall({
                    otherPartyId: selectedDMProfile.userId,
                    otherPartyName: selectedDMProfile.displayName,
                    otherPartyCourse: selectedDMProfile.course,
                    otherPartyAvatar: selectedDMProfile.avatarUrl,
                    otherPartyPhone: selectedDMProfile.phone,
                    isVideo: false,
                  })} disabled={!canUseWorkspace} aria-label="Voice call">
                    <FontAwesomeIcon icon={faPhone} />
                  </button>
                  <button type="button" onClick={() => void openWorkspaceConversationActions()} disabled={!canUseWorkspace} aria-label="More options">
                    <span>⋯</span>
                  </button>
                </div>
              </div>

              <div className="workspaceConversationBody">
                <div className="workspaceChatDateChip">
                  <span>{messages.length > 0 ? 'Today' : 'No messages yet'}</span>
                </div>

                {messages.length === 0 ? (
                  <div className="workspaceConversationEmpty">
                    <div className="workspaceConversationEmptyIcon">
                      <FontAwesomeIcon icon={faCommentDots} />
                    </div>
                    <h3>No messages yet</h3>
                    <p>Send the first message to start the conversation.</p>
                  </div>
                ) : (
                  <div className="workspaceConversationMessages" onClick={() => setActiveReactionPickerId('')}>
                    {messages.map((message, index) => {
                      const isMine = Boolean(message.isMine)
                      const messageId = firstNonEmpty(message.messageId, message.id, '')
                      const reactionSummary = getReactionSummary(message)
                      const previewLabel = getMessageViewLabel(message)
                      const attachmentType = firstNonEmpty(message.attachmentType, '').toLowerCase()
                      const hasAttachment = Boolean(firstNonEmpty(message.attachmentUrl, ''))
                      const isPickerOpen = activeReactionPickerId === messageId

                      return (
                        <div
                          key={messageId || `${message.senderId || 'message'}-${message.clientTimestamp || message.serverTimestamp || index}`}
                          className={isMine ? 'workspaceConversationRow workspaceConversationRow--mine' : 'workspaceConversationRow workspaceConversationRow--incoming'}
                          onContextMenu={(event) => {
                            event.preventDefault()
                            if (isMine) {
                              void openWorkspaceMessageActions(message)
                            } else {
                              openReactionPicker(messageId)
                            }
                          }}
                          onPointerDown={() => {
                            scheduleReactionPicker(messageId)
                          }}
                          onPointerUp={clearLongPressTimer}
                          onPointerCancel={clearLongPressTimer}
                          onPointerLeave={clearLongPressTimer}
                        >
                          <div className={isMine ? 'workspaceConversationBubble workspaceConversationBubble--mine' : 'workspaceConversationBubble workspaceConversationBubble--incoming'}>
                            {hasAttachment && attachmentType === 'image' && (
                              <button
                                type="button"
                                className="workspaceConversationAttachmentImage"
                                onClick={() => openAttachmentPreview('image', message.attachmentUrl, message.attachmentName || 'Photo')}
                              >
                                <img src={message.attachmentUrl} alt={message.attachmentName || 'Attachment'} />
                              </button>
                            )}

                            {hasAttachment && attachmentType === 'video' && (
                              <button
                                type="button"
                                className="workspaceConversationAttachmentVideo"
                                onClick={() => openAttachmentPreview('video', message.attachmentUrl, message.attachmentName || 'Video')}
                              >
                                <span>▶</span>
                                <strong>{message.attachmentName || 'Video'}</strong>
                                <p>Tap to open</p>
                              </button>
                            )}

                            {hasAttachment && attachmentType === 'audio' && (
                              <button
                                type="button"
                                className="workspaceConversationAttachmentAudio"
                                onClick={() => openAudioUrl(message.attachmentUrl)}
                              >
                                <span>🎤</span>
                                <strong>{message.attachmentName || 'Voice message'}</strong>
                                <p>Tap to play</p>
                              </button>
                            )}

                            {hasAttachment && attachmentType === 'file' && (
                              <button
                                type="button"
                                className="workspaceConversationAttachmentFile"
                                onClick={() => openAttachmentUrl(message.attachmentUrl)}
                              >
                                <span>📄</span>
                                <strong>{message.attachmentName || 'File'}</strong>
                                <p>Tap to open</p>
                              </button>
                            )}

                            {message.text && (
                              <div
                                className="workspaceConversationBubbleText"
                                dangerouslySetInnerHTML={{ __html: message.text }}
                              />
                            )}

                            {reactionSummary.length > 0 && (
                              <div className="workspaceConversationReactions">
                                {reactionSummary.map((reaction) => (
                                  <button
                                    key={`${messageId}-${reaction.emoji}`}
                                    type="button"
                                    className={reaction.reactedByMe ? 'workspaceConversationReaction active' : 'workspaceConversationReaction'}
                                    onClick={(event) => handleReactionPointerDown(event, message, reaction.emoji)}
                                  >
                                    <span>{reaction.emoji}</span>
                                    <strong>{reaction.count}</strong>
                                  </button>
                                ))}
                              </div>
                            )}

                            <div className="workspaceConversationFooter">
                              <time>{formatWorkspaceChatTime(getMessageTimestamp(message))}</time>
                              {isMine && <span>{previewLabel}</span>}
                              {message.isEdited && <span>Edited</span>}
                            </div>

                            {isPickerOpen && (
                              <div className="workspaceConversationActionStrip">
                                {REACTION_OPTIONS.map((emoji) => (
                                  <button
                                    key={`${messageId}-${emoji}`}
                                    type="button"
                                    onClick={(event) => handleReactionPointerDown(event, message, emoji)}
                                  >
                                    {emoji}
                                  </button>
                                ))}
                                <button type="button" onClick={() => void openWorkspaceMessageActions(message)}>
                                  ⋯
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      )
                    })}
                    <div ref={messagesEndRef} />
                  </div>
                )}
              </div>

              <div className="workspaceConversationComposer">
                {editingMessageId && (
                  <div className="workspaceConversationEditBanner">
                    <strong>Editing message</strong>
                    <button type="button" onClick={clearEditMode}>Cancel</button>
                  </div>
                )}

                <div className="workspaceConversationComposerRow">
                  <button type="button" onClick={() => toast.info('Emoji picker will be wired into the composer next.')} aria-label="Emoji">
                    🙂
                  </button>
                  <button type="button" onClick={() => toast.info('Attachment upload will match the mobile flow next.')} aria-label="Attach">
                    📎
                  </button>
                  <div
                    className="workspaceConversationEditor"
                    ref={editorRef}
                    contentEditable={canUseWorkspace}
                    suppressContentEditableWarning
                    onInput={updateEditorState}
                    onBlur={updateEditorState}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' && !event.shiftKey) {
                        event.preventDefault()
                        void sendMessage()
                      }
                    }}
                    data-placeholder={editingMessageId ? 'Edit message' : 'Type a message'}
                  />
                  {newMessage.trim() || selectedAttachmentUri ? (
                    <button type="button" className="workspaceConversationSendButton" onClick={() => void sendMessage()} disabled={!canUseWorkspace}>
                      <FontAwesomeIcon icon={faPaperPlane} />
                    </button>
                  ) : (
                    <button type="button" className="workspaceConversationMicButton" onClick={() => void toggleVoiceRecording()} disabled={!canUseWorkspace}>
                      <FontAwesomeIcon icon={faMicrophone} />
                    </button>
                  )}
                </div>

                {selectedAttachmentUri && (
                  <div className="workspaceConversationAttachmentPreview">
                    <div>
                      <strong>{selectedAttachmentName || 'Attachment'}</strong>
                      <span>{selectedAttachmentType || 'file'}</span>
                    </div>
                    <button type="button" onClick={clearSelectedAttachment}>Remove</button>
                  </div>
                )}
              </div>
            </div>
          ) : activeWorkspaceSection === 'updates' ? (
            <>
              <input
                ref={statusMediaInputRef}
                type="file"
                accept="image/*,video/*"
                style={{ display: 'none' }}
                onChange={handleWorkspaceStatusMediaSelection}
              />

              {workspaceStatusComposerOpen ? (
                <div className="workspaceStatusComposer">
                  <div className="workspaceStatusComposerHeader">
                    <button type="button" className="workspaceStatusIconButton" onClick={closeWorkspaceStatusComposer} aria-label="Close composer">
                      <FontAwesomeIcon icon={faXmark} />
                    </button>
                    <div>
                      <strong>Create status</strong>
                      <p>Post a story with text, photo, or video.</p>
                    </div>
                    <button
                      type="button"
                      className="workspaceStatusPrimaryButton"
                      onClick={() => void publishWorkspaceStatus()}
                      disabled={workspaceStatusComposerPublishing}
                    >
                      {workspaceStatusComposerPublishing ? `Posting ${workspaceStatusComposerProgress}%` : 'Post'}
                    </button>
                  </div>

                  <div className="workspaceStatusProfileCard">
                    <div className="workspaceStatusProfileAvatarShell">
                      <img
                        className="workspaceStatusProfileAvatar"
                        src={currentUserAvatar || sampleImage}
                        alt={currentUserName || 'Student'}
                      />
                    </div>
                    <div className="workspaceStatusProfileCopy">
                      <strong>{currentUserName || 'Student'}</strong>
                      <span>{currentUserCourse || 'Course not set'}</span>
                    </div>
                  </div>

                  <label className="workspaceStatusTextareaShell" htmlFor="workspaceStatusCaption">
                    <span>Caption</span>
                    <textarea
                      id="workspaceStatusCaption"
                      value={workspaceStatusComposerCaption}
                      onChange={(event) => setWorkspaceStatusComposerCaption(event.target.value)}
                      placeholder="What's on your mind?"
                    />
                  </label>

                  <div className="workspaceStatusColorRow">
                    {STATUS_COLORS.map((color, index) => {
                      const active = index === workspaceStatusComposerColorIndex
                      return (
                        <button
                          key={color}
                          type="button"
                          className={`workspaceStatusColorChip ${active ? 'workspaceStatusColorChip--active' : ''}`}
                          style={{ backgroundColor: color }}
                          onClick={() => setWorkspaceStatusComposerColorIndex(index)}
                          aria-label={`Select status color ${index + 1}`}
                        />
                      )
                    })}
                  </div>

                  {workspaceStatusComposerPreviewUrl && (
                    <div className="workspaceStatusMediaPreview">
                      {workspaceStatusComposerMediaType === 'video' ? (
                        <video src={workspaceStatusComposerPreviewUrl} controls playsInline />
                      ) : (
                        <img src={workspaceStatusComposerPreviewUrl} alt="Status preview" />
                      )}
                    </div>
                  )}

                  <div className="workspaceStatusComposerActions">
                    <button type="button" className="workspaceStatusSecondaryButton" onClick={triggerWorkspaceStatusMediaPicker} disabled={workspaceStatusComposerPublishing}>
                      Choose media
                    </button>
                    <button type="button" className="workspaceStatusSecondaryButton" onClick={clearWorkspaceStatusMedia} disabled={workspaceStatusComposerPublishing || !workspaceStatusComposerMediaFile}>
                      Clear media
                    </button>
                  </div>

                  {workspaceStatusComposerPublishing && (
                    <div className="workspaceStatusProgressBar">
                      <span style={{ width: `${workspaceStatusComposerProgress}%` }} />
                    </div>
                  )}
                </div>
              ) : workspaceStatusViewerOpen ? (
                <>
                  <div className="workspaceStatusViewer">
                    <div className="workspaceStatusViewerProgressRow">
                      {workspaceStatusViewerStories.map((story, index) => (
                        <span
                          key={story.statusId}
                          className={index <= workspaceStatusViewerIndex ? 'workspaceStatusViewerProgress workspaceStatusViewerProgress--active' : 'workspaceStatusViewerProgress'}
                        />
                      ))}
                    </div>

                    <div className="workspaceStatusViewerHeader">
                      <div className="workspaceStatusViewerProfile">
                        <img src={firstNonEmpty(currentWorkspaceStatusStory?.avatarUrl, sampleImage)} alt={currentWorkspaceStatusStory?.displayName || 'Student'} />
                        <div>
                          <strong>{firstNonEmpty(currentWorkspaceStatusStory?.displayName, 'Student')}</strong>
                          <span>
                            {firstNonEmpty(currentWorkspaceStatusStory?.course, 'Course not set')}
                            {' • '}
                            {currentWorkspaceStatusStory ? formatStoryTime(currentWorkspaceStatusStory.createdAtMs) : 'Just now'}
                          </span>
                        </div>
                      </div>

                      <div className="workspaceStatusViewerActions">
                        {currentWorkspaceStatusStory?.isMine && (
                          <button type="button" className="workspaceStatusViewerIcon" onClick={() => void openWorkspaceStatusViewers()} aria-label="View viewers">
                            <FontAwesomeIcon icon={faEye} />
                          </button>
                        )}
                        {currentWorkspaceStatusStory?.isMine && (
                          <button
                            type="button"
                            className="workspaceStatusViewerIcon workspaceStatusViewerIcon--danger"
                            onClick={confirmAndDeleteWorkspaceStatus}
                            aria-label="Delete status"
                          >
                            <FontAwesomeIcon icon={faTrash} />
                          </button>
                        )}
                        <button type="button" className="workspaceStatusViewerIcon" onClick={closeWorkspaceStatusViewer} aria-label="Close viewer">
                          <FontAwesomeIcon icon={faXmark} />
                        </button>
                      </div>
                    </div>

                    <div className="workspaceStatusViewerStage">
                      <button
                        type="button"
                        className="workspaceStatusViewerNav workspaceStatusViewerNav--left"
                        onClick={goToPreviousWorkspaceStatus}
                        aria-label="Previous status"
                        disabled={workspaceStatusViewerIndex <= 0}
                      >
                        <FontAwesomeIcon icon={faChevronLeft} />
                      </button>

                      <div className="workspaceStatusViewerCanvas">
                        {workspaceStatusViewerLoading && (
                          <div className="workspaceStatusViewerEmpty">
                            <p>Loading story...</p>
                          </div>
                        )}

                        {!workspaceStatusViewerLoading && !currentWorkspaceStatusStory && (
                          <div className="workspaceStatusViewerEmpty">
                            <p>No story to show.</p>
                          </div>
                        )}

                        {!workspaceStatusViewerLoading && currentWorkspaceStatusStory && renderWorkspaceStatusStory(currentWorkspaceStatusStory)}
                      </div>

                      <button
                        type="button"
                        className="workspaceStatusViewerNav workspaceStatusViewerNav--right"
                        onClick={goToNextWorkspaceStatus}
                        aria-label="Next status"
                        disabled={workspaceStatusViewerIndex >= workspaceStatusViewerStories.length - 1}
                      >
                        <FontAwesomeIcon icon={faChevronRight} />
                      </button>
                    </div>

                    <div className="workspaceStatusViewerFooter">
                      <div className="workspaceStatusViewerMeta">
                        <strong>
                          {currentWorkspaceStatusStory ? `${currentWorkspaceStatusStory.activeCount > 1 ? `${currentWorkspaceStatusStory.activeCount} updates` : 'Update'}` : 'Update'}
                        </strong>
                        <span>
                          {currentWorkspaceStatusStory ? getWorkspaceStatusRelativeMeta(currentWorkspaceStatusStory) : 'Just now'}
                        </span>
                      </div>

                      <div className="workspaceStatusViewerHint">
                        Tap the sides or use the arrows to move between stories.
                      </div>
                    </div>
                  </div>

                  {workspaceStatusViewersOpen && (
                    <div className="workspaceStatusScrim" role="presentation" onClick={closeWorkspaceStatusViewers}>
                      <div
                        className="workspaceStatusViewers"
                        role="dialog"
                        aria-modal="true"
                        aria-label="Status viewers"
                        onClick={(event) => event.stopPropagation()}
                      >
                        <div className="workspaceStatusViewersHeader">
                          <div>
                            <strong>Viewers</strong>
                            <p>{workspaceStatusViewersStatus?.displayName || 'This status'}</p>
                          </div>
                          <button type="button" className="workspaceStatusIconButton" onClick={closeWorkspaceStatusViewers} aria-label="Close viewers">
                            <FontAwesomeIcon icon={faXmark} />
                          </button>
                        </div>

                        <div className="workspaceStatusViewersList">
                          {workspaceStatusViewersLoading && (
                            <div className="workspaceStatusViewerEmpty">
                              <p>Loading viewers...</p>
                            </div>
                          )}

                          {!workspaceStatusViewersLoading && workspaceStatusViewers.length === 0 && (
                            <div className="workspaceStatusViewerEmpty">
                              <p>No viewers yet.</p>
                            </div>
                          )}

                          {workspaceStatusViewers.map((viewer) => (
                            <div key={viewer.id} className="workspaceStatusViewerRow">
                              <img src={firstNonEmpty(viewer.avatarUrl, sampleImage)} alt={viewer.displayName} />
                              <div className="workspaceStatusViewerRowCopy">
                                <strong>{viewer.displayName}</strong>
                                <span>{firstNonEmpty(viewer.course, 'Course not set')}</span>
                              </div>
                              <div className="workspaceStatusViewerRowTime">
                                {viewer.viewedAtMs ? formatStoryTime(viewer.viewedAtMs) : ''}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </>
              ) : null}
            </>
          ) : (
            <div className="workspacePreviewCard">
              <div className="workspacePreviewIllustration">
                <div className="workspacePreviewDevice">
                  <span className="workspacePreviewDeviceTop" />
                  <span className="workspacePreviewDeviceScreen">
                    <FontAwesomeIcon icon={activeWorkspaceSectionData.icon} />
                  </span>
                </div>
              </div>
              <h2>{activeWorkspaceSectionData.label}</h2>
              <p>
                {activeWorkspaceSection === 'contacts' && 'Choose someone from the list to start a conversation.'}
                {activeWorkspaceSection === 'chats' && 'Select a conversation from the list to start chatting.'}
                {activeWorkspaceSection === 'updates' && 'View the mobile-style status feed directly inside the workspace.'}
                {activeWorkspaceSection === 'channels' && 'Channel details and posts will live here next.'}
                {activeWorkspaceSection === 'calls' && 'Open the mobile-style calls inbox to view recent audio and video sessions.'}
              </p>
              <div className="workspacePreviewActions">
                <button type="button" onClick={activeWorkspaceSectionData.action} disabled={!canUseWorkspace}>
                  {activeWorkspaceSectionData.actionLabel}
                </button>
                <button type="button" className="secondary" onClick={() => navigate('/dashboard')}>
                  Back to dashboard
                </button>
              </div>
              <div className="workspacePreviewQuickActions">
                <button type="button">
                  <span>📄</span>
                  <strong>Send document</strong>
                </button>
                <button type="button" onClick={openStudentPicker} disabled={!canUseWorkspace}>
                  <span>👤</span>
                  <strong>Add contact</strong>
                </button>
                <button
                  type="button"
                  onClick={() => {
                    if (activeWorkspaceSection === 'calls') {
                      setActiveWorkspaceSection('calls')
                      return
                    }
                    if (activeWorkspaceSection === 'updates') {
                      void openWorkspaceStatusComposer()
                      return
                    }
                    setActiveWorkspaceSection('updates')
                  }}
                >
                  <span>●</span>
                  <strong>{activeWorkspaceSection === 'calls' ? 'Open calls' : activeWorkspaceSection === 'updates' ? 'Add status' : 'Open updates'}</strong>
                </button>
              </div>
              {activeWorkspaceSection === 'updates' && selectedWorkspaceStatus && (
                <div className="workspacePreviewStoryCard">
                  <div className="workspacePreviewStoryTop">
                    <img src={selectedWorkspaceStatus.avatarUrl || sampleImage} alt="" />
                    <div>
                      <strong>{selectedWorkspaceStatus.displayName || 'Student'}</strong>
                      <span>{selectedWorkspaceStatus.course || 'Course unknown'}</span>
                    </div>
                    <time>{formatWorkspaceStatusClock(selectedWorkspaceStatus.createdAtMs) || 'Recently'}</time>
                  </div>
                  <p>{selectedWorkspaceStatus.caption || 'Status update'}</p>
                  <div className="workspacePreviewStoryMeta">
                    <span>
                      {selectedWorkspaceStatus.activeCount > 1
                        ? `${selectedWorkspaceStatus.activeCount} updates`
                        : selectedWorkspaceStatus.isViewedByMe
                          ? 'Seen'
                          : 'New'}
                    </span>
                    <span>
                      {selectedWorkspaceStatus.viewCount <= 0
                        ? '0 views'
                        : selectedWorkspaceStatus.viewCount === 1
                          ? '1 view'
                          : `${selectedWorkspaceStatus.viewCount} views`}
                    </span>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </section>
    </div>
  )
}

export default WorkspaceComp
